import React, { useState, useEffect, useRef } from 'react';
import YouTube from 'react-youtube';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword, updateProfile, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { getFirestore, collection, setDoc, getDocs, addDoc, doc, updateDoc, increment, getDoc  } from 'firebase/firestore/lite'; // 添加 getDoc 方法的引入
import headerImage from './images/header.webp';
import headerImage2 from './images/header-2.webp';
import headerImage3 from './images/header-3.webp';
import firebaseConfig from './firebaseConfig';
import messages from './messages';
import './App.css';
import Marquee from "react-fast-marquee";
import CustomPlaylistEditor from './CustomPlaylistEditor';
import { extractMusicUrl, detectMusicUrlType } from './MusicLinkHelper';  // 🔴 導入 VV 音樂處理工具
import { Converter } from 'opencc-js';
const t2s = Converter({ from: 'tw', to: 'cn' });
const s2t = Converter({ from: 'cn', to: 'tw' });

// 🔴 如果导入失败，在这里定义 extractYouTubeVideoId
const extractYouTubeVideoId = (url) => {
  if (!url) return null;
  try {
    if (url.includes('youtube.com')) {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('v');
    }
    if (url.includes('youtu.be')) {
      return url.split('youtu.be/')[1].split('?')[0];
    }
    return null;
  } catch (error) {
    console.error('提取 YouTube ID 时出错:', error);
    return null;
  }
};


// ===== 编号排序工具（支持 '0001' 与 '0001a' 等混合） =====
const parseBianHao = (val) => {
  const s = (val ?? '').toString().trim().toLowerCase();
  const m = s.match(/^(\d+)([a-z]?)$/);
  if (!m) return { num: Number.NaN, suffix: '', raw: s };
  return { num: parseInt(m[1], 10), suffix: m[2] || '', raw: s };
};
const compareBianHaoRaw = (va, vb, dir = 'asc') => {
  const a = parseBianHao(va);
  const b = parseBianHao(vb);
  // both not numeric -> fallback to locale string compare
  if (Number.isNaN(a.num) && Number.isNaN(b.num)) {
    const cmp0 = (a.raw || '').localeCompare(b.raw || '', undefined, { numeric: true, sensitivity: 'base' });
    return dir === 'asc' ? cmp0 : -cmp0;
  }
  if (Number.isNaN(a.num)) return dir === 'asc' ? 1 : -1;
  if (Number.isNaN(b.num)) return dir === 'asc' ? -1 : 1;
  if (a.num !== b.num) return dir === 'asc' ? a.num - b.num : b.num - a.num;
  // same numeric part: base (no suffix) should come before variants (e.g. 0001 < 0001a)
  const aBase = a.suffix === '';
  const bBase = b.suffix === '';
  if (aBase !== bBase) return dir === 'asc' ? (aBase ? -1 : 1) : (aBase ? 1 : -1);
  // both have suffix (or both empty), compare suffix alphabetically
  const cmp = a.suffix.localeCompare(b.suffix, undefined, { sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
};
// ===== /编号排序工具 =====


const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const firestore = getFirestore(app);



// 新增讀取瀏覽次數的函數
const fetchPageViews = async () => {
  const docRef = doc(db, 'viewCounts', 'pageViews');
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return docSnap.data().pageViews;
  } else {
    return 0;
  }
};


// ============================================================================
// 🔴 混合音樂播放器組件 - 支持 VV 音樂和 YouTube
// ============================================================================
const HybridMusicPlayer = ({ 
  url, 
  width = '100%', 
  height = 500,
  // 🔴 新增的播放控制參數
  playerTime = 0,
  onReady = null,
  onStateChange = null,
  onEnd = null,
  containerClassName = 'youtubeContainer'
}) => {
  // 🔴 添加調試代碼
  //console.log('🎵 HybridMusicPlayer 被調用');
  //console.log('📍 url:', url);
  //console.log('📍 playerTime:', playerTime);
  
  if (!url) {
    console.warn('⚠️ HybridMusicPlayer: URL 為空');
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#f9f9f9',
        borderRadius: '8px',
        border: '1px solid #ddd',
        color: '#999',
        fontSize: '14px'
      }}>
        無效的播放鏈結
      </div>
    );
  }

  // 🔴 新增：判斷是否只是 YouTube Video ID（如 dQw4w9WgXcQ）
  const isJustVideoId = /^[a-zA-Z0-9_-]{11}$/.test(url.trim());
  let processedUrl = url;
  let type = detectMusicUrlType(url);
  
  // 如果是純 Video ID，轉換為完整 YouTube URL
  if (isJustVideoId) {
    //console.log('📍 檢測到純 YouTube Video ID:', url);
    processedUrl = `https://www.youtube.com/watch?v=${url}`;
    type = 'youtube';
  }
  
  //console.log('📍 URL 類型:', type);
  //console.log('📍 處理後的 URL:', processedUrl);

  if (type === 'vv') {
    return (
      <div 
        className={containerClassName}
        style={{
          width: '100%',
          aspectRatio: '16/9',
          border: '1px solid #ddd',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5'
        }}>
        <iframe
          width="100%"
          height="100%"
          src={processedUrl}
          frameBorder="0"
          allow="autoplay; accelerometer; cipboard-write; enlcrypted-media; gyroscope; picture-in-picture; volume; fullscreen"
          allowFullScreen
          title="VV Music Player"
          style={{ border: 'none', display: 'block', height: '100%' }}
        />
      </div>
    );
  } else if (type === 'youtube') {
    //console.log('✅ 使用 YouTube 播放器');
    // YouTube 播放器 - 使用 YouTube React 组件，保留所有原有功能
    const videoId = isJustVideoId ? url.trim() : extractYouTubeVideoId(processedUrl);
    //console.log('📍 YouTube Video ID:', videoId);
    
    if (!videoId) {
      console.error('❌ 無法提取 YouTube Video ID');
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#ffe6e6',
          borderRadius: '8px',
          border: '1px solid #ff9999',
          color: '#cc0000',
          fontSize: '14px'
        }}>
          無法提取 YouTube Video ID
        </div>
      );
    }

    // 🔴 保留原有的 YouTube 播放器，帶有所有播放控制功能
    return (
      <YouTube
        key="hybrid-youtube-player"
        videoId={videoId}
        containerClassName={containerClassName}
        opts={{
          width: '100%',
          playerVars: {
            autoplay: 1,
            start: playerTime,  // 🔴 保留起始時間
            playsinline: 1,     // 視頻在iOS設備上播放時能自動播放
            // 🔴 新增：音量和字幕支持
            fs: 1,              // 允許全屏
            controls: 1,        // 顯示播放器控制
            iv_load_policy: 3,  // 禁用視頻註解
            modestbranding: 0,  // 顯示 YouTube 標誌
            rel: 0,             // 禁止相關視頻
            showinfo: 0,        // 隱藏視頻信息
            cc_load_policy: 1   // 🔴 默認載入字幕（如果有）
          }
        }}
        onEnd={onEnd}                          // 🔴 保留結束事件
        onStateChange={onStateChange}          // 🔴 保留狀態改變事件
        onReady={onReady}                      // 🔴 保留就緒事件
      />
    );
  } else {
    console.warn('⚠️ 不支持的鏈結類型:', type);
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#fff3cd',
        borderRadius: '8px',
        border: '1px solid #ffc107',
        color: '#856404',
        fontSize: '14px'
      }}>
        ⚠️ 不支持的鏈結類型，請檢查 URL
      </div>
    );
  }
};


const UserDefineButton = ({
  auth,
  firestore,
  displayLanguage,
  originalHymnList,
  customPlaylistNames,
  setCustomPlaylistNames,
  setHymnList,
  setSortOrder,
  setCurrentIndex,
  setSearchInfo,
  defaultSearchInfo,
  setButtonColor,
  buttonColor = {}, // 给 buttonColor 一个默认值
  setPlaylistActive,
  exitIPADPlaylistMode,
  youtubePlaylists,      // ✅ 新增
  setYoutubePlaylists,   // ✅ 新增
  fetchUserCoverLinks,   // ✅ 新增：接收函數
  currentCategory,       // ✅ 新增
  email: parentEmail,    // ✅ 新增：重命名避免衝突
  hymnList,              // ✅ 新增
  currentIndex: parentCurrentIndex,  // ✅ 新增：重命名避免衝突
  setCurrentVideoId,     // ✅ 新增
  userCoverLinks,        // ✅ 新增
  coverSongMode,         // ✅ 新增：翻唱歌曲模式
  setCoverSongMode,      // ✅ 新增：設置翻唱歌曲模式
  originalHymnListCopy,  // ✅ 新增：原始歌單副本
  onDisplayLanguageChange // 🔴 新增：語言模式變更函數
}) => {
  const [isCustomPlaylistOpen, setIsCustomPlaylistOpen] = useState(false);
  const [customPlaylistsSongs, setCustomPlaylistsSongs] = useState([[], [], [], [], []]); // ✅ 新增：跟蹤每個自選歌單的歌曲

  useEffect(() => {
    const fetchCustomPlaylistNames = async () => {
      if (auth.currentUser) {
        const userDocRef = doc(firestore, 'users', auth.currentUser.uid);

        try {
          const userDocSnapshot = await getDoc(userDocRef);
          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();
            if (userData.customPlaylists) {
              setCustomPlaylistNames(userData.customPlaylists.map(pl => pl.name));
              // ✅ 新增：同時載入自選歌單的歌曲數據
              const playlistsSongs = userData.customPlaylists.map(pl => pl.songs || []);
              setCustomPlaylistsSongs(playlistsSongs);
            } else {
              setCustomPlaylistNames([]);
              setCustomPlaylistsSongs([[], [], [], [], []]);
            }
          } else {
            setCustomPlaylistNames([]);
            setCustomPlaylistsSongs([[], [], [], [], []]);
          }
        } catch (error) {
          console.error('Error fetching custom playlist names:', error);
          setCustomPlaylistNames([]);
          setCustomPlaylistsSongs([[], [], [], [], []]);
        }
      } else {
        setCustomPlaylistNames([]);
        setCustomPlaylistsSongs([[], [], [], [], []]);
      }
    };

    fetchCustomPlaylistNames();
  }, [auth.currentUser, firestore, setCustomPlaylistNames]);

  useEffect(() => {
    // Reset display name and custom playlists on logout
    if (!auth.currentUser) {
      setCustomPlaylistNames([]);
      setCustomPlaylistsSongs([[], [], [], [], []]); // ✅ 新增：登出時清空自選歌單歌曲數據
    }
  }, [auth.currentUser, setCustomPlaylistNames]);

  const getButtonName = (name, index) => {
    if (auth.currentUser && typeof name === 'string') {
      return name;
    }
    if (displayLanguage === '简体') {
      return `自选歌单${index + 1}`;
    }
    if (displayLanguage === '繁體') {
      return `自選歌單${index + 1}`;
    }
    return `Your playlist ${index + 1}`;
  };

  const getEditButtonLabel = () => {
    if (displayLanguage === '简体') {
      return '编辑歌单';
    }
    if (displayLanguage === '繁體') {
      return '編輯歌單';
    }
    return 'Edit Playlist';
  };

  const initialPlaylistNames = Array.from({ length: 5 }, (_, index) => getButtonName('', index));

  const fetchCustomPlaylistSongs = async (index) => {
    if (auth.currentUser) {
      const userDocRef = doc(firestore, 'users', auth.currentUser.uid);
      try {
        const userDocSnapshot = await getDoc(userDocRef);
        if (userDocSnapshot.exists()) {
          const userData = userDocSnapshot.data();
          if (userData.customPlaylists && userData.customPlaylists[index]) {
            const playlistSongs = userData.customPlaylists[index].songs;
            const currentSelectSongs = originalHymnList.filter(hymn => playlistSongs.includes(hymn.编号)); // 使用 '编号' 字段
            
            if (currentSelectSongs.length > 0) {
              setHymnList(currentSelectSongs);
              setSortOrder('desc');
              setCurrentIndex(0);
              setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
              setButtonColor(prevState => {
                const newButtonColor = {
                  ...prevState,
                  sing: 'deepskyblue',  //導唱
                  accompaniment: 'bisque', //伴奏
                  cover: 'bisque',      //翻唱
                  shortVideo: 'bisque', //短視頻
                  referenceVideo: 'bisque', //參考視頻
                  search: 'darkorange', //搜尋
                  all: 'yellow',   //全部歌曲
                  cantonese: 'yellow',  //粵語歌曲
                  english: 'yellow',    //英文歌曲
                  select: 'yellow',     //精選
                  highquility: 'yellow', //優質伴奏
                  harmony: 'yellow',    //和聲歌曲
                  custom1: index === 0 ? 'deepskyblue' : 'orange',    //自選歌單1
                  custom2: index === 1 ? 'deepskyblue' : 'orange',    //自選歌單2
                  custom3: index === 2 ? 'deepskyblue' : 'orange',    //自選歌單3
                  custom4: index === 3 ? 'deepskyblue' : 'orange',    //自選歌單4
                  custom5: index === 4 ? 'deepskyblue' : 'orange',    //自選歌單5
                  worship: 'gold',      //敬拜
                  praise: 'gold',       //讚美
                  thanks: 'gold',       //感恩
                  desire: 'gold',       //生命圣诗
                  ipadPlay: 'LightSalmon', //iPad連續播放
                  playlist1: 'LightSalmon',  // 🔴 播放清單1
                  playlist2: 'LightSalmon',  // 🔴 播放清單2
                  playlist3: 'LightSalmon',  // 🔴 播放清單3
                  playlist4: 'LightSalmon',  // 🔴 播放清單4
                  playlist5: 'LightSalmon',  // 🔴 播放清單5
                };

                return newButtonColor;
              });
            } else {
              const playlistName = customPlaylistNames[index];
              let noSongsMessage;
              if (displayLanguage === '简体') {
                noSongsMessage = `${playlistName} 里面没有歌曲`;
              } else if (displayLanguage === '繁體') {
                noSongsMessage = `${playlistName} 裡面沒有歌曲`;
              } else {
                noSongsMessage = `There are no songs in ${playlistName}`;
              }
              setSearchInfo(noSongsMessage);
            }
          } else {
            //console.log('No songs found for this playlist.');
          }
        } else {
          //console.log('User document does not exist.');
        }
      } catch (error) {
        //console.error('Error fetching custom playlist songs:', error);
      }
    } else {
      //console.log('No authenticated user.');
    }
  };

  const handleButtonClick = (index) => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式  
    fetchCustomPlaylistSongs(index).then(() => {
    });
  };

  useEffect(() => {
  }, [buttonColor]);

  return (
    <>
      {/* 🔴 第6排：自選歌單按鈕 - 均勻分配布局（與第7排相同） */}
      <div style={{ display: 'flex', gap: '1px', padding: '2px', marginTop: '0', marginBottom: '2px', width: '100%', boxSizing: 'border-box' }}>
        {auth.currentUser
          ? customPlaylistNames.length > 0
            ? customPlaylistNames.map((playlist, index) => {
                // ✅ 改進：檢查該自選歌單是否有歌曲
                const hasPlaylistSongs = customPlaylistsSongs[index] && customPlaylistsSongs[index].length > 0;
                const isDisabled = !hasPlaylistSongs;
                
                return (
                  <button
                    key={index}
                    style={{ 
                      flex: 1,
                      margin: '0 1px',
                      borderRadius: '10px', 
                      backgroundColor: buttonColor[`custom${index + 1}`] || 'orange',
                      fontSize: '100%', 
                      fontWeight: 'bold',
                      opacity: isDisabled ? 0.5 : 1,  // ✅ disabled時灰顯
                      cursor: isDisabled ? 'not-allowed' : 'pointer'  // ✅ 改變光標
                    }}
                    onClick={() => !isDisabled && handleButtonClick(index)}
                    disabled={isDisabled}
                  >
                    {getButtonName(playlist, index)}
                  </button>
                );
              })
            : initialPlaylistNames.map((name, index) => (
                <button
                  key={index}
                  style={{ 
                    flex: 1,
                    margin: '0 1px',
                    borderRadius: '10px', 
                    backgroundColor: buttonColor[`custom${index + 1}`] || 'orange', 
                    fontSize: '100%', 
                    fontWeight: 'bold',
                    opacity: 0.5,
                    cursor: 'not-allowed'
                  }}
                  onClick={() => {}}
                  disabled={true}
                >
                  {name}
                </button>
              ))
          : initialPlaylistNames.map((name, index) => (
              <button 
                key={index} 
                style={{ 
                  flex: 1,
                  margin: '0 1px',
                  borderRadius: '10px', 
                  backgroundColor: buttonColor[`custom${index + 1}`] || 'orange', 
                  fontSize: '100%', 
                  fontWeight: 'bold',
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }} 
                disabled
              >
                {name}
              </button>
            ))}
        <button 
          style={{ 
            flex: 1,
            margin: '0 1px',
            backgroundColor: 'tomato', 
            fontSize: '100%', 
            fontWeight: 'bold',
            borderRadius: '10px'
          }} 
          onClick={() => setIsCustomPlaylistOpen(true)} 
          disabled={!auth.currentUser}
        >
          {getEditButtonLabel()}
        </button>
      </div>
      {isCustomPlaylistOpen && (
        <div className="modal-overlay">
          <div className="custom-playlist-modal" style={{ position: 'absolute', left: '0', top: '0' }}>
            <CustomPlaylistEditor
              auth={auth}
              firestore={firestore}
              displayLanguage={displayLanguage}
              originalHymnList={originalHymnList}
              setIsCustomPlaylistOpen={setIsCustomPlaylistOpen}
              customPlaylistNames={customPlaylistNames}
              setCustomPlaylistNames={setCustomPlaylistNames}
              youtubePlaylists={youtubePlaylists}
              setYoutubePlaylists={setYoutubePlaylists}
              customPlaylistsSongs={customPlaylistsSongs}        // ✅ 新增
              setCustomPlaylistsSongs={setCustomPlaylistsSongs}  // ✅ 新增
              coverSongMode={coverSongMode}                      // ✅ 新增：翻唱歌曲模式
              setCoverSongMode={setCoverSongMode}                // ✅ 新增：設置翻唱歌曲模式
              originalHymnListCopy={originalHymnListCopy}        // ✅ 新增：原始歌單副本
              onDisplayLanguageChange={onDisplayLanguageChange}  // 🔴 新增：語言模式變更函數
              onSaveSuccess={async () => {
                // ✅ 保存成功後立即重新載入使用者翻唱連結和模式，並更新歌單
                if (auth.currentUser) {
                  await fetchUserCoverLinks(auth.currentUser.uid);
                  // 更新歌單會由 useEffect 自動處理
                }
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};





const LoginModal = ({ displayLanguage, handleGoogleLogin, handleEmailPasswordLogin, handleSignUp, handleResetPassword, onClose }) => {
  const [emailModal, setEmailModal] = useState('');
  const [passwordModal, setPasswordModal] = useState('');
  const [displayNameModal, setDisplayNameModal] = useState(''); // 新增 displayName 的輸入狀態

  return (
    <div style={{ position: 'fixed', top: '30%', left: '30%', transform: 'translate(-50%, -50%)', backgroundColor: 'white', padding: '10px', boxShadow: '0px 0px 10px rgba(0, 0, 0, 0)', borderRadius: '10px', zIndex: '999', border: '2px solid grey', textAlign: 'center' }}>
      {/* 关闭窗口按钮 */}
      <div style={{ position: 'absolute', top: '0px', right: '0px', fontSize: '20px', cursor: 'pointer', padding: '5px', backgroundColor: '#f0f0f0', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20px', height: '20px', lineHeight: '20px', hover: { backgroundColor: 'grey' } }} onClick={onClose}>X</div>
      <h3 style={{ fontSize: '120%', marginBottom: '10px', marginRight: '10px' }}>{(displayLanguage === '简体' ? '登入帐号' : (displayLanguage === '繁體' ? '登入帳號' : 'Login Account'))}</h3>
      <button style={{ fontSize: '100%', marginBottom: '5px', marginRight: '10px' }} onClick={() => handleGoogleLogin()}>{(displayLanguage === '简体' ? 'Google帳号登入' : (displayLanguage === '繁體' ? 'Google帳號登入' : 'Google account login'))}</button><br />
      <span style={{ fontSize: '100%', marginBottom: '5px', marginRight: '10px' }}>{(displayLanguage === '简体' ? '或' : (displayLanguage === '繁體' ? '或' : 'or'))}</span><br />
      <input style={{ fontSize: '100%', marginBottom: '5px', marginLeft: '10px', marginRight: '10px' }} type="email" placeholder={(displayLanguage === '简体' ? '输入邮箱' : (displayLanguage === '繁體' ? '輸入郵箱' : 'Enter email'))} value={emailModal} onChange={(e) => setEmailModal(e.target.value)} /><br />
      <input style={{ fontSize: '100%', marginBottom: '5px', marginLeft: '10px', marginRight: '10px' }} type="password" placeholder={(displayLanguage === '简体' ? '输入密码' : (displayLanguage === '繁體' ? '輸入密碼' : 'Enter password'))} value={passwordModal} onChange={(e) => setPasswordModal(e.target.value)} /><br />
      <input style={{ fontSize: '100%', marginBottom: '5px', marginLeft: '10px', marginRight: '10px' }} type="text" placeholder={(displayLanguage === '简体' ? '输入显示名称' : (displayLanguage === '繁體' ? '輸入顯示名稱' : 'Enter display name'))} value={displayNameModal} onChange={(e) => setDisplayNameModal(e.target.value)} /><br /> {/* 新增 displayName 的输入框 */}
      <div>
        <button style={{ fontSize: '100%', marginBottom: '5px', marginRight: '20px' }} onClick={() => handleEmailPasswordLogin(emailModal, passwordModal, displayNameModal)}>{(displayLanguage === '简体' ? '登入' : (displayLanguage === '繁體' ? '登入' : 'Login'))}</button>
        <button style={{ fontSize: '100%', marginBottom: '5px', marginRight: '10px' }} onClick={() => handleSignUp(emailModal, passwordModal, displayNameModal)}>{(displayLanguage === '简体' ? '注册' : (displayLanguage === '繁體' ? '註冊' : 'Register'))}</button>
      </div>
      <button style={{ fontSize: '100%', marginBottom: '5px', marginRight: '10px' }} onClick={() => handleResetPassword(emailModal)}>{(displayLanguage === '简体' ? '重设密码' : (displayLanguage === '繁體' ? '重設密碼' : 'Reset Password'))}</button><br />
      <button style={{ fontSize: '100%', marginBottom: '5px', marginRight: '10px' }} onClick={onClose}>{(displayLanguage === '简体' ? '关闭窗口' : (displayLanguage === '繁體' ? '關閉視窗' : 'Close Window'))}</button>
    </div>
  );
};




// 🎵 可拖移、可調整大小的歌詞視窗（自動隨 currentIndex 重新抓歌詞）
const LyricsDraggableModal = ({ displayLanguage, hymnList, currentIndex, currentCategory, onClose }) => {
  // 初始位置：右側，頂部對齊歌曲清單；底部留 200px 空間避免遮住圖片
  const BOTTOM_MARGIN = 200; // 底部留白（圖片＋版本欄）
  const TOP_START = 140;     // 頂部起始 y
  const initH = Math.max(200, window.innerHeight - BOTTOM_MARGIN - TOP_START);
  const [pos, setPos] = React.useState({ x: window.innerWidth - 340, y: TOP_START });
  const [size, setSize] = React.useState({ w: 300, h: initH });

  // 🔴 首次掛載時清除舊格式 cache（key 沒有 :own/:ref 的舊版本）
  React.useEffect(() => {
    try {
      const toDelete = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('ytLyricsDesc:') && !key.endsWith(':own') && !key.endsWith(':ref')) {
          toDelete.push(key);
        }
      }
      toDelete.forEach(k => localStorage.removeItem(k));
    } catch (e) { /* ignore */ }
  }, []);

  // 歌詞狀態（自己管理）
  const [rawLyrics, setRawLyrics] = React.useState('');  // 原始繁體歌詞（永遠存繁體）
  const [lyrics, setLyrics] = React.useState('');
  const [lyricsLoading, setLyricsLoading] = React.useState(false);
  const [lyricsError, setLyricsError] = React.useState('');
  const [copyDone, setCopyDone] = React.useState(false);  // 複製成功提示

  // 當 displayLanguage 或 rawLyrics 變動時，重新做簡繁轉換（不重新 fetch）
  React.useEffect(() => {
    if (!rawLyrics) return;
    const isReferenceVideo = currentCategory === '参考链结';
    if (!isReferenceVideo && displayLanguage === '简体') {
      setLyrics(t2s(rawLyrics));
    } else {
      setLyrics(rawLyrics);
    }
  }, [displayLanguage, rawLyrics, currentCategory]);

  // 當 currentIndex 或 currentCategory 變動時自動重新抓歌詞
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const currentSongData = hymnList[currentIndex];
    if (!currentSongData) return;

    // 是否為「參考視頻」（別人的影片，顯示全部說明）
    const isReferenceVideo = currentCategory === '参考链结';

    // 🔴 修正：非參考類別只用當前類別的連結，不做跨類別 fallback
    // （避免抓到別的類別的影片說明）
    const tryLinks = isReferenceVideo
      ? [currentSongData['参考链结']]
      : [currentSongData[currentCategory]];  // 只用當前類別，不 fallback

    let videoId = null;
    for (const link of tryLinks) {
      if (!link || link.trim() === '') continue;
      const extracted = extractYouTubeVideoId(link);
      if (extracted) { videoId = extracted; break; }
      if (/^[a-zA-Z0-9_-]{11}$/.test(link.trim())) { videoId = link.trim(); break; }
    }

    if (!videoId) {
      setRawLyrics('');
      setLyrics('');
      setLyricsError(
        isReferenceVideo
          ? (displayLanguage === '简体' ? '找不到YouTube视频ID，无法读取歌词' :
             displayLanguage === '繁體' ? '找不到YouTube影片ID，無法讀取歌詞' :
             'Cannot find YouTube video ID to load lyrics')
          : (displayLanguage === '简体' ? '此类别没有影片连结，无法读取歌词' :
             displayLanguage === '繁體' ? '此類別沒有影片連結，無法讀取歌詞' :
             'No video link for this category')
      );
      return;
    }

    setLyricsLoading(true);
    setLyricsError('');
    setRawLyrics('');
    setLyrics('');

    const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`;
    const ytApiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
    if (!ytApiKey) {
      // 若沒設定金鑰，就跳過 YouTube Data API，只走後備抓網頁
      console.warn('REACT_APP_YOUTUBE_API_KEY 沒設定，將使用後備方式抓歌詞說明欄。');
    }

    const parseDescription = (html) => {
      const match = html.match(/"shortDescription":"((?:[^"\\]|\\.)*)"/);
      if (!match) return null;
      return match[1]
        .replace(/\\n/g, '\n')
        .replace(/\\u0026/g, '&')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
    };

    // 帶 timeout 的 fetch（避免慢 proxy 卡住）
    const fetchWithTimeout = async (url, timeoutMs, isJson = false) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const resp = await fetch(url, { signal: controller.signal });
        if (!resp.ok) return null;
        const data = isJson ? await resp.json() : await resp.text();
        return data;
      } catch (e) {
        return null;
      } finally {
        clearTimeout(timer);
      }
    };

    const getLyricsFromDescription = (description) => {
      if (!description) return null;
      const lines = description.split('\n');
      // 參考視頻：顯示全部說明；自己的視頻：從第5行開始
      // 永遠回傳原始繁體，轉換由 displayLanguage useEffect 統一處理
      return (isReferenceVideo ? lines : lines.slice(4)).join('\n').trim();
    };

    // 🔴 cache key 加入 ref 標記，避免同一 videoId 在不同類別讀到錯誤的 slice 結果
    const cacheKey = `ytLyricsDesc:${videoId}:${isReferenceVideo ? 'ref' : 'own'}`;
    let cancelled = false;
    try {
      const cachedDescription = localStorage.getItem(cacheKey);
      if (cachedDescription) {
        const lyricsText = getLyricsFromDescription(cachedDescription);
        if (!cancelled) {
          if (!lyricsText) {
            setLyricsError(
              displayLanguage === '简体' ? '说明栏没有内容' :
              displayLanguage === '繁體' ? '說明欄沒有內容' :
              'No content found in the description'
            );
          } else {
            setRawLyrics(lyricsText);
          }
          setLyricsLoading(false);
        }
        return;
      }
    } catch (e) {
      // localStorage 可能在某些瀏覽器設定/隱私模式下不可用
    }

    const resolveFirstNonEmptyString = async (promises) => {
      const wrapped = promises.map((p) =>
        p.then((v) => {
          if (typeof v === 'string' && v.trim()) return v;
          throw new Error('empty');
        })
      );
      try {
        return await Promise.any(wrapped);
      } catch {
        return null;
      }
    };

    const fetchYoutubeDescriptionViaYouTubeAPI = async () => {
      if (!ytApiKey) return null;
      const apiUrl = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${ytApiKey}`;
      const timeoutMs = 8000;

      const parseJsonFromText = (text) => {
        if (!text) return null;
        const t = String(text).trim();
        try {
          if (t.startsWith('{')) return JSON.parse(t);
        } catch {
          // ignore
        }
        const firstBrace = t.indexOf('{');
        const lastBrace = t.lastIndexOf('}');
        if (firstBrace >= 0 && lastBrace > firstBrace) {
          try {
            return JSON.parse(t.slice(firstBrace, lastBrace + 1));
          } catch {
            return null;
          }
        }
        return null;
      };

      const directP = fetchWithTimeout(apiUrl, timeoutMs, true).then((res) => {
        return res?.items?.[0]?.snippet?.description ?? null;
      });

      const alloriginsApiUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(apiUrl)}`;
      const alloriginsP = fetchWithTimeout(alloriginsApiUrl, timeoutMs, true).then((res) => {
        try {
          const contents = res?.contents;
          const parsed = typeof contents === 'string' ? JSON.parse(contents) : contents;
          return parsed?.items?.[0]?.snippet?.description ?? null;
        } catch {
          return null;
        }
      });

      const corsproxyApiUrl = `https://corsproxy.io/?${encodeURIComponent(apiUrl)}`;
      const corsproxyP = fetchWithTimeout(corsproxyApiUrl, timeoutMs, true).then((res) => {
        return res?.items?.[0]?.snippet?.description ?? null;
      });

      // 追加：行動網路常見會擋掉部分代理域名，r.jina.ai 通常較穩
      const jinaApiUrlReal = `https://r.jina.ai/https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${ytApiKey}`;
      const jinaP = fetchWithTimeout(jinaApiUrlReal, timeoutMs, false).then((text) => {
        const parsed = parseJsonFromText(text);
        return parsed?.items?.[0]?.snippet?.description ?? null;
      });

      return await resolveFirstNonEmptyString([directP, alloriginsP, corsproxyP, jinaP]);
    };

    const tryFetch = async () => {
      // 優先使用 YouTube Data API（通常比抓 YouTube 網頁快很多）
      const ytApiDescription = await fetchYoutubeDescriptionViaYouTubeAPI();
      if (ytApiDescription) return ytApiDescription;

      // 後備：沿用原本「抓 YouTube 網頁短描述」的解析方式（並行競速）
      const timeoutMs = 8000;

      const directHtmlP = fetchWithTimeout(youtubeUrl, timeoutMs, false).then((html) => {
        if (!html) return null;
        return parseDescription(html);
      });

      const alloriginsHtmlApiUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(youtubeUrl)}`;
      const alloriginsHtmlP = fetchWithTimeout(alloriginsHtmlApiUrl, timeoutMs, true).then((res) => {
        if (!res?.contents) return null;
        return parseDescription(res.contents);
      });

      const corsproxyHtmlP = fetchWithTimeout(
        `https://corsproxy.io/?${encodeURIComponent(youtubeUrl)}`,
        timeoutMs,
        false
      ).then((html) => {
        if (!html) return null;
        return parseDescription(html);
      });

      const jinaHtmlUrlReal = `https://r.jina.ai/https://www.youtube.com/watch?v=${videoId}`;
      const jinaHtmlP = fetchWithTimeout(jinaHtmlUrlReal, timeoutMs, false).then((html) => {
        if (!html) return null;
        return parseDescription(html);
      });

      return await resolveFirstNonEmptyString([directHtmlP, alloriginsHtmlP, corsproxyHtmlP, jinaHtmlP]);
    };

    tryFetch()
      .then((description) => {
        if (cancelled) return;
        if (!description) {
          setLyricsError(
            displayLanguage === '简体' ? '无法读取歌词，请直接前往YouTube查看说明栏' :
            displayLanguage === '繁體' ? '無法讀取歌詞，請直接前往YouTube查看說明欄' :
            'Cannot read lyrics. Please visit YouTube to view the description'
          );
          return;
        }

        const lyricsText = getLyricsFromDescription(description);
        if (!lyricsText) {
          setLyricsError(
            displayLanguage === '简体' ? '说明栏没有内容' :
            displayLanguage === '繁體' ? '說明欄沒有內容' :
            'No content found in the description'
          );
        } else {
          // 只快取「description」，但必須確保 slicing 後真的有內容，避免緩存到空結果
          try {
            localStorage.setItem(cacheKey, description);
          } catch (e) {
            // ignore quota/blocked errors
          }
          setRawLyrics(lyricsText);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setLyricsError(
          displayLanguage === '简体' ? '读取歌词时发生错误，请直接前往YouTube查看' :
          displayLanguage === '繁體' ? '讀取歌詞時發生錯誤，請直接前往YouTube查看' :
          'Error loading lyrics. Please visit YouTube directly'
        );
      })
      .finally(() => {
        if (!cancelled) setLyricsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentIndex, hymnList, currentCategory]);
  const dragging = React.useRef(false);
  const resizing = React.useRef(false);
  const dragStart = React.useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const resizeStart = React.useRef({ mx: 0, my: 0, w: 0, h: 0 });

  const onDragMouseDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    // touch
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onMouseUp);
  };

  const onResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizing.current = true;
    resizeStart.current = { mx: e.clientX, my: e.clientY, w: size.w, h: size.h };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onMouseUp);
  };

  const onMouseMove = (e) => {
    if (dragging.current) {
      const dx = e.clientX - dragStart.current.mx;
      const dy = e.clientY - dragStart.current.my;
      setPos({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
    }
    if (resizing.current) {
      const dx = e.clientX - resizeStart.current.mx;
      const dy = e.clientY - resizeStart.current.my;
      setSize({ w: Math.max(200, resizeStart.current.w + dx), h: Math.max(150, resizeStart.current.h + dy) });
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length !== 1) return;
    e.preventDefault();
    onMouseMove(e.touches[0]);
  };

  const onMouseUp = () => {
    dragging.current = false;
    resizing.current = false;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onMouseUp);
  };

  const songNum = hymnList[currentIndex]?.编号 || '';
  const songName = hymnList[currentIndex]
    ? (displayLanguage === '简体' ? hymnList[currentIndex].歌名 : hymnList[currentIndex].繁體歌名)
    : '';
  // 🎵 簡體模式顯示簡體歌名（t2s 轉換），其他模式原樣
  const displaySongName = `${songNum}  ${(displayLanguage === '简体' && songName) ? t2s(songName) : songName}`;

  return (
    <div
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 6px 30px rgba(0,0,0,0.35)',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '2px solid plum',
        userSelect: 'text',  // 允許選取複製歌詞
      }}
    >
      {/* 拖移標題列 */}
      <div
        onMouseDown={onDragMouseDown}
        onTouchStart={(e) => {
          const t = e.touches[0];
          dragging.current = true;
          dragStart.current = { mx: t.clientX, my: t.clientY, px: pos.x, py: pos.y };
          window.addEventListener('touchmove', onTouchMove, { passive: false });
          window.addEventListener('touchend', onMouseUp);
        }}
        style={{
          backgroundColor: 'plum',
          color: 'white',
          padding: '6px 10px',
          cursor: 'move',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          userSelect: 'none',  // 拖移區不可選取
        }}
      >
        <span style={{ fontWeight: 'bold', fontSize: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', flex: 1 }}>
          🎵&nbsp;&nbsp;{displaySongName}
        </span>
        {/* 複製全部歌詞按鈕 */}
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => {
            const textToCopy = `${displaySongName}\n\n${lyrics}`;
            navigator.clipboard.writeText(textToCopy).then(() => {
              setCopyDone(true);
              setTimeout(() => setCopyDone(false), 2000);
            }).catch(() => {
              // fallback：建立暫時 textarea 複製
              const ta = document.createElement('textarea');
              ta.value = textToCopy;
              ta.style.position = 'fixed';
              ta.style.opacity = '0';
              document.body.appendChild(ta);
              ta.select();
              document.execCommand('copy');
              document.body.removeChild(ta);
              setCopyDone(true);
              setTimeout(() => setCopyDone(false), 2000);
            });
          }}
          title={displayLanguage === '简体' ? '复制全部歌词' : displayLanguage === '繁體' ? '複製全部歌詞' : 'Copy all lyrics'}
          style={{
            border: 'none',
            background: copyDone ? 'rgba(100,255,100,0.4)' : 'rgba(255,255,255,0.25)',
            color: 'white',
            borderRadius: '6px',
            padding: '2px 7px',
            fontSize: '13px',
            cursor: 'pointer',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '4px',
            transition: 'background 0.3s',
          }}
        >
          {copyDone
            ? (displayLanguage === '简体' ? '✓已复制' : displayLanguage === '繁體' ? '✓已複製' : '✓Copied')
            : '📋'}
        </button>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={onClose}
          style={{
            border: 'none',
            background: 'rgba(255,255,255,0.25)',
            color: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            fontSize: '14px',
            cursor: 'pointer',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: '6px',
          }}
        >✕</button>
      </div>

      {/* 歌詞內容 */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '10px 12px',
          whiteSpace: 'pre-wrap',
          fontSize: '100%',
          lineHeight: '1.75',
          color: '#222',
          backgroundColor: '#fafafa',
        }}
      >
        {lyricsLoading && (
          <div style={{ textAlign: 'center', color: '#999', paddingTop: '20px' }}>
            {displayLanguage === '简体' ? '正在读取歌词...' : displayLanguage === '繁體' ? '正在讀取歌詞...' : 'Loading lyrics...'}
          </div>
        )}
        {!lyricsLoading && lyricsError && (
          <div style={{ color: '#c00', padding: '8px' }}>{lyricsError}</div>
        )}
        {!lyricsLoading && !lyricsError && lyrics}
      </div>

      {/* 右下角拖移調整大小控點 */}
      <div
        onMouseDown={onResizeMouseDown}
        onTouchStart={(e) => {
          const t = e.touches[0];
          e.stopPropagation();
          resizing.current = true;
          resizeStart.current = { mx: t.clientX, my: t.clientY, w: size.w, h: size.h };
          window.addEventListener('touchmove', onTouchMove, { passive: false });
          window.addEventListener('touchend', onMouseUp);
        }}
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: '18px',
          height: '18px',
          cursor: 'se-resize',
          background: 'transparent',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          padding: '2px',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="plum">
          <path d="M0 12 L12 0 L12 12 Z" opacity="0.5"/>
          <path d="M4 12 L12 4 L12 12 Z" opacity="0.7"/>
          <path d="M8 12 L12 8 L12 12 Z" opacity="1"/>
        </svg>
      </div>
    </div>
  );
};

const HymnListView = () => {
  const [hymnList, setHymnList] = useState([]);
  const [originalHymnList, setOriginalHymnList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ===== 單曲播放用 =====
  // eslint-disable-next-line no-unused-vars
  const [currentVideoId, setCurrentVideoId] = useState('');

  // 🔴 新增：開機封面動畫（純視覺疊加層，不影響任何播放狀態）
  // showIntroSplash 控制是否顯示；introSplashShrink 控制是否已開始退場動畫
  // 時間軸：靜止 1.5 秒 → 退場動畫（旋轉+弧線飄移+縮小）1.5 秒 → 關閉畫面（總長 3 秒）
  const [showIntroSplash, setShowIntroSplash] = useState(true);
  const [introSplashShrink, setIntroSplashShrink] = useState(false);

  // 🔴 隨機退場動畫參數：每次頁面掛載時只計算一次（lazy initial state，不會在重新渲染時改變）。
  // 新動畫邏輯：logo 先隨機飄向某個方向（弧線繞路），最終收斂飛入 headerImage 右上角的 logo 位置並縮小消失。
  // 這樣視覺上 splash logo 就像「飛回」到 header 圖片上的 logo 徽章，增強品牌一致感。
  // 目標位置依版面而異：
  //   寬螢幕（>700px）：header 圖在右側容器底部，logo 位於圖片右上角 ≈ (94vw, 82vh)
  //   窄螢幕（≤700px）：header 圖在頁面最下方，logo 位於圖片右上角 ≈ (86vw, 72vh)
  // 偏移量從 splash logo 中心（50vw, 50vh）計算，分別換算為 vw/vh 的 translate 值。
  // 中途關鍵影格（40%）加入隨機繞路偏移，製造先飄再收的弧線感。
  const [introAnim] = useState(() => {
    const isWide = window.innerWidth > 700;
    // 目標位置（整個視窗座標，vw/vh）
    // headerImage 右上角 logo 在畫面中的估算位置
    const targetX = isWide ? 44 : 36;   // translate X（vw），正值=向右
    const targetY = isWide ? 32 : 22;   // translate Y（vh），正值=向下
    // 隨機繞路方向：從四個象限選一個（左上、右上、左下、右下），但不能與目標方向太像
    // 以免「繞路點」與目標點同向，造成繞路不明顯
    const detourDirections = [
      { dx: -30, dy: -25 }, // 左上繞路
      { dx:  25, dy: -30 }, // 右上繞路
      { dx: -35, dy:  20 }, // 左下繞路
    ];
    const detour = detourDirections[Math.floor(Math.random() * detourDirections.length)];
    // 旋轉：退場時帶一點自旋增加動感，方向隨機，角度適中（不過快）
    const spinDir = Math.random() < 0.5 ? -1 : 1;
    const spinAngle = spinDir * (120 + Math.floor(Math.random() * 120)); // 120°~240°
    return {
      targetX,
      targetY,
      detourX: detour.dx,
      detourY: detour.dy,
      spinAngle,
    };
  });

  useEffect(() => {
    // 先靜止顯示 1.5 秒，讓使用者看清楚完整圖案
    const shrinkTimer = setTimeout(() => setIntroSplashShrink(true), 1500);
    // 退場動畫本身需時 1.5 秒（與下方 @keyframes 動畫時長一致），結束後徹底移除這個圖層
    // （避免擋住下方版面的點擊/觸控）
    const removeTimer = setTimeout(() => setShowIntroSplash(false), 3000);
    return () => {
      clearTimeout(shrinkTimer);
      clearTimeout(removeTimer);
    };
  }, []);

  const [currentIndex, setCurrentIndex] = useState(0);
  // ===== iPad 官方播放清單模式（解決 iPad 無法連續播放）=====
  const HYMN_PLAYLIST_ID = 'PL4GpUQOn9sZBH39CZ5QLj_ysBxvmFcKCi';
  const HYMN_SHORT_VIDEO_PLAYLIST_ID = 'PL4GpUQOn9sZDLZZx6qWKPZ5aM_rjcQskn'; // 詩歌短視頻清單
  const [playlistActive, setPlaylistActive] = useState(false);
  const [isPlayingShortVideoPlaylist, setIsPlayingShortVideoPlaylist] = useState(true); // 初始為true，第一次按才會切換到原唱清單

  const [currentCategory, setCurrentCategory] = useState('原唱链结');
  const [playerTime, setPlayerTime] = useState(0);
  const [lastPlayerState, setLastPlayerState] = useState(YouTube.PlayerState.UNSTARTED);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInfo, setSearchInfo] = useState(''); 
  const [isLooping, setIsLooping] = useState(false);
  const [is20SecondsLooping, setIs20SecondsLooping] = useState(false); // 新增 20 秒循环状态
  const [searchedSong, setSearchedSong] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [player, setPlayer] = useState(null);
  const [loopTimer, setLoopTimer] = useState(null);
  const [currentPlayingUrl, setCurrentPlayingUrl] = useState(null);  // 🔴 當前播放的 URL (VV 音樂或 YouTube)
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortColumn, setSortColumn] = useState('编号');
  const [showLspColumn, setShowLspColumn] = useState(false); // 生命聖詩欄位顯示控制
  const [currentSong, setCurrentSong] = useState(null);
  const [hasClicked, setHasClicked] = useState(false); // 新增一個狀態變數來追蹤是否已經點擊過按鈕  
  const [defaultSearchInfo, setDefaultSearchInfo] = useState(''); // 新增一個狀態來存儲初始的跑馬燈訊息
  const [buttonColor, setButtonColor] = useState({
    sing: 'deepskyblue',  //導唱
    accompaniment: 'bisque', //伴奏
    cover: 'bisque',      //翻唱
    shortVideo: 'bisque', //短視頻
    referenceVideo: 'bisque', //參考視頻
    search: 'darkorange', //搜尋
    all: 'deepskyblue',   //全部歌曲
    cantonese: 'yellow',  //粵語歌曲
    english: 'yellow',    //英文歌曲
    select: 'yellow',     //精選
    highquility: 'yellow', //優質伴奏
    harmony: 'yellow',    //和聲歌曲
    custom1: 'orange',    //自選歌單1
    custom2: 'orange',    //自選歌單2
    custom3: 'orange',    //自選歌單3
    custom4: 'orange',    //自選歌單4
    custom5: 'orange',    //自選歌單5
    worship: 'gold',      //敬拜
    praise: 'gold',       //讚美
    thanks: 'gold',       //感恩
    desire: 'gold',       //生命圣诗
    ipadPlay: 'LightSalmon', //iPad連續播放
    playlist1: 'LightSalmon',  // 🔴 播放清單1
    playlist2: 'LightSalmon',  // 🔴 播放清單2
    playlist3: 'LightSalmon',  // 🔴 播放清單3
    playlist4: 'LightSalmon',  // 🔴 播放清單4
    playlist5: 'LightSalmon',  // 🔴 播放清單5
  });

  const [backupButtonColor, setBackupButtonColor] = useState({}); //備份進入iPad連續播放前的按鍵顏色

  const [headerImages, setHeaderImages] = useState({
    简体: headerImage,
    繁體: headerImage2,
    English: headerImage3
  });

  const languages = ['简体', '繁體', 'English'];
  // 🔴 語言模式初始值：優先讀取瀏覽器本機儲存的設定，找不到才使用預設值「繁體」
  const [displayLanguage, setDisplayLanguage] = useState(() => {
    try {
      const savedLanguage = localStorage.getItem('displayLanguage');
      if (savedLanguage && languages.includes(savedLanguage)) {
        return savedLanguage;
      }
    } catch (error) {
      console.error('讀取本機語言設定失敗:', error);
    }
    return '繁體';
  });
  const languageIndex = languages.indexOf(displayLanguage);

  // 🔴 語言模式變更：更新畫面顯示、寫入本機儲存，並（若已登入）同步寫入資料庫
  const handleDisplayLanguageChange = async (lang) => {
    if (!languages.includes(lang)) return;
    setDisplayLanguage(lang);
    try {
      localStorage.setItem('displayLanguage', lang);
    } catch (error) {
      console.error('儲存本機語言設定失敗:', error);
    }
    if (auth.currentUser) {
      try {
        const userDocRef = doc(firestore, 'users', auth.currentUser.uid);
        await setDoc(userDocRef, { displayLanguage: lang }, { merge: true });
      } catch (error) {
        console.error('儲存語言設定到資料庫失敗:', error);
      }
    }
  };

  // 🔴 載入使用者已儲存在資料庫中的語言模式設定
  const fetchDisplayLanguage = async (userId) => {
    try {
      const userDocRef = doc(firestore, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (userData.displayLanguage && languages.includes(userData.displayLanguage)) {
          setDisplayLanguage(userData.displayLanguage);
          try {
            localStorage.setItem('displayLanguage', userData.displayLanguage);
          } catch (error) {
            console.error('儲存本機語言設定失敗:', error);
          }
        }
      }
    } catch (error) {
      console.error('讀取資料庫語言設定失敗:', error);
    }
  };
  const leftContainer = useRef(null);
  const rightContainer = useRef(null);
  const [email, setEmail] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [displayName, setDisplayName] = useState(''); // 新增 displayName 狀態變量
  const [currentUser, setCurrentUser] = useState(null);
  const [customPlaylistNames, setCustomPlaylistNames] = useState([
    '自选歌单1', '自选歌单2', '自选歌单3', '自选歌单4', '自选歌单5'
  ]);
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 700);
  // 🔴 新增:YouTube 播放清單狀態
  const [youtubePlaylists, setYoutubePlaylists] = useState(['', '', '', '', '']);
  // ✅ 新增：使用者翻唱連結
  const [userCoverLinks, setUserCoverLinks] = useState({});
  // ✅ 新增：翻唱歌曲模式 (1=只用自己輸入, 2=優先使用自己輸入, 3=使用系統內建)
  const [coverSongMode, setCoverSongMode] = useState(2); // 預設模式2
  // ✅ 新增：原始歌單的備份副本，避免頻繁存取Firebase
  const [originalHymnListCopy, setOriginalHymnListCopy] = useState([]);

  // 🎵 歌詞功能相關狀態
  const [showLyricsModal, setShowLyricsModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);  // 🔴 訊息欄點擊跳出視窗



  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth > 700);
    };

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);








  const handleThematicButtonClick = (theme) => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式  
    // 过滤歌曲列表并更新状态
    const filteredList = originalHymnList.filter(hymn => hymn[theme] && hymn[theme].toString().trim() !== '');
    setHymnList(filteredList);
    setCurrentCategory('原唱链结');
    // 按下生命聖詩時顯示LSP欄位，否則顯示原唱欄位
    setShowLspColumn(theme === '生命圣诗');
  
    setButtonColor((prevColors) => {
      const newColors = {
        ...prevColors,
        sing: 'deepskyblue',  // 导唱
        accompaniment: 'bisque', // 伴奏
        cover: 'bisque',      // 翻唱
        shortVideo: 'bisque', // 短視頻
        referenceVideo: 'bisque', // 参考视频
        search: 'darkorange', // 搜索
        all: 'yellow',   // 全部歌曲
        cantonese: 'yellow',  // 粤语歌曲
        english: 'yellow',    // 英文歌曲
        select: 'yellow',     // 精选
        highquility: 'yellow', // 优质伴奏
        harmony: 'yellow',    // 和声歌曲
        custom1: 'orange',    // 自选歌单1
        custom2: 'orange',    // 自选歌单2
        custom3: 'orange',    // 自选歌单3
        custom4: 'orange',    // 自选歌单4
        custom5: 'orange',    // 自选歌单5
        ipadPlay: 'LightSalmon', //iPad連續播放
        playlist1: 'LightSalmon',  // 🔴 播放清單1
        playlist2: 'LightSalmon',  // 🔴 播放清單2
        playlist3: 'LightSalmon',  // 🔴 播放清單3
        playlist4: 'LightSalmon',  // 🔴 播放清單4
        playlist5: 'LightSalmon',  // 🔴 播放清單5
      };
  
      const worshipButtons = {
        '敬拜': 'worship',
        '赞美': 'praise',
        '感恩': 'thanks',
        '生命圣诗': 'desire',
      };
  
      Object.keys(worshipButtons).forEach((key) => {
        newColors[worshipButtons[key]] = key === theme ? 'deepskyblue' : 'gold';
      });
  
      return newColors;
    });
  };




  // 🎵 歌詞按鈕 - 開啟歌詞視窗（視窗內部自行抓取歌詞）
  const handleLyricsButtonClick = () => {
    setShowLyricsModal(true);
  };
  const fetchYoutubePlaylists = async (userId) => {
    try {
      const userDocRef = doc(firestore, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        if (Array.isArray(userData.youtubePlaylists)) {
          setYoutubePlaylists(userData.youtubePlaylists);
        }
      }
    } catch (error) {
      console.error('Error fetching YouTube playlists:', error);
    }
  };

  // ✅ 新增：載入使用者翻唱連結和模式的函數
  const fetchUserCoverLinks = async (userId) => {
    try {
      const userDocRef = doc(firestore, 'users', userId);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        
        // 載入翻唱歌曲模式
        if (userData.coverSongMode && typeof userData.coverSongMode === 'number') {
          setCoverSongMode(userData.coverSongMode);
        } else {
          setCoverSongMode(2); // 預設模式2
        }
        
        // 載入使用者翻唱連結
        if (userData.userCoverLinks && typeof userData.userCoverLinks === 'object') {
          setUserCoverLinks(userData.userCoverLinks);
          return userData.userCoverLinks; // ✅ 返回最新的連結
        }
      }
      return {}; // ✅ 如果沒有數據，返回空對象
    } catch (error) {
      console.error('Error fetching user cover links:', error);
      return {};
    }
  };

  // ✅ 手機從 Google 登入頁面返回後，處理 Redirect 登入結果
  useEffect(() => {
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          const user = result.user;
          setIsLoggedIn(true);
          setDisplayName(user.displayName);
          setEmail(user.email);
          const userDocRef = doc(firestore, 'users', user.uid);
          const userDocSnapshot = await getDoc(userDocRef);
          if (!userDocSnapshot.exists()) {
            const userData = {
              displayName: user.displayName,
              email: user.email,
              photoURL: user.photoURL,
              displayLanguage: displayLanguage, // 🔴 新增：儲存目前的語言模式
              customPlaylists: {
                playlist1: '自選歌單1',
                playlist2: '自選歌單2',
                playlist3: '自選歌單3',
                playlist4: '自選歌單4',
                playlist5: '自選歌單5',
              }
            };
            await setDoc(userDocRef, userData);
            setCustomPlaylistNames(Object.values(userData.customPlaylists));
          } else {
            const userData = userDocSnapshot.data();
            setCustomPlaylistNames(Object.values(userData.customPlaylists || {}));
          }
        }
      } catch (error) {
        console.error('Redirect 登入處理失敗:', error);
      }
    };
    handleRedirectResult();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
    if (!user) {
      setCurrentUser(null);
      setIsLoggedIn(false);
      setDisplayName('');
      setEmail('');
      setCustomPlaylistNames([
        '自选歌单1', '自选歌单2', '自选歌单3', '自选歌单4', '自选歌单5'
      ]);
      setYoutubePlaylists(['', '', '', '', '']); // ✅ 新增：登出時清空播放清單
      setUserCoverLinks({}); // ✅ 新增：登出時清空翻唱連結
    } else {
      setCurrentUser(user);
      setIsLoggedIn(true);
      setDisplayName(user.displayName || '');

      // 🔹 如果 auth 讀不到 email，改從 Firestore 讀
      if (user.email) {
        setEmail(user.email);
      } else {
        try {
          const userDocRef = doc(firestore, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setEmail(userData.email || '');
          }
        } catch (error) {
          console.error("讀取 Firestore email 失敗:", error);
        }
      }

      setIsModalOpen(false); // 登入成功後關閉模態框
      fetchCustomPlaylistNames(user.uid);
      fetchYoutubePlaylists(user.uid); // ✅ 新增：載入 YouTube 播放清單
      fetchUserCoverLinks(user.uid); // ✅ 新增：載入使用者翻唱連結
      fetchDisplayLanguage(user.uid); // 🔴 新增：載入使用者儲存的語言模式設定
    }
  });
  
  return () => unsubscribe();
  }, []);
  
  
  
  const fetchCustomPlaylistNames = async (userId) => {
    try {
      const userDocRef = doc(firestore, 'users', userId);
      const userDocSnapshot = await getDoc(userDocRef);
      if (userDocSnapshot.exists()) {
        const userData = userDocSnapshot.data();
        if (userData.customPlaylists) {
          setCustomPlaylistNames(userData.customPlaylists.map(playlist => playlist.name));
        }
      }
    } catch (error) {
      console.error('Error fetching custom playlist names:', error);
    }
  };



  const handleLogout = async () => {
    await signOut(auth);
    setCurrentUser(null);
    setDisplayName('');
    setCustomPlaylistNames([
      '自选歌单1', '自选歌单2', '自选歌单3', '自选歌单4', '自选歌单5'
    ]);
    setIsLoggedIn(false); // 設置 isLoggedIn 為 false
  };

  const renderLoginButton = () => {
    if (currentUser) {
      return (
        <button onClick={handleLogout} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '12%', fontWeight: 'bold', backgroundColor: isLoggedIn ? 'SpringGreen' : 'Thistle', margin: '0', padding: '0', fontSize: '100%' }}>
          {displayLanguage === '简体' ? '登出' : (displayLanguage === '繁體' ? '登出' : 'Logout')}
        </button>
      );
    } else {
      return (
        <button onClick={() => setIsModalOpen(true)} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '12%', fontWeight: 'bold', backgroundColor: isLoggedIn ? 'SpringGreen' : 'Thistle', margin: '0', padding: '0', fontSize: '100%' }}>
          {displayLanguage === '简体' ? '请登入' : (displayLanguage === '繁體' ? '請登入' : 'Login')}
        </button>
      );
    }
  };
  





  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    setSearchInfo(defaultSearchInfo); 
  };


  // 加載初始瀏覽次數
  useEffect(() => {
    fetchPageViews(); // 仍執行以確保初始化，但不存入狀態
  }, []);

  // 在組件掛載時添加事件監聽器,第一次按鍵才增加瀏覽次數
  useEffect(() => {
    const handleClick = () => {
      if (!hasClicked) {
        updatePageViews();
        setHasClicked(true);
      }
    };
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [hasClicked]);

  // 更新瀏覽次數
  const updatePageViews = async () => {
    try {
      const docRef = doc(db, 'viewCounts', 'pageViews');
      await updateDoc(docRef, { pageViews: increment(1) });
    } catch (error) {
      console.error('Error updating page views:', error);
    }
  };



  const handleLanguageToggle = () => {
      const nextLanguageIndex = (languageIndex + 1) % languages.length;
      handleDisplayLanguageChange(languages[nextLanguageIndex]); // 🔴 修正：改用會存檔的函數，而不是只切換畫面
  };
  





  useEffect(() => {
    // 根據 displayLanguage 來設定對應的訊息
    const message = displayLanguage === '简体' ? messages['简体讯息'] : (displayLanguage === '繁體' ? messages['繁體訊息'] : messages['英文訊息']);
    setSearchInfo(message);
  
    // 更新顯示的圖片
    setHeaderImages({
      简体: headerImage,
      繁體: headerImage2,
      English: headerImage3
    });
  }, [displayLanguage]);

  useEffect(() => {
    // 根據 displayLanguage 來設定對應的跑馬燈訊息
    const defaultMessage = displayLanguage === '简体' ? messages['简体讯息'] : (displayLanguage === '繁體' ? messages['繁體訊息'] : messages['英文訊息']);
    setDefaultSearchInfo(defaultMessage); // 初始化時設置為對應的跑馬燈訊息
    setSearchInfo(defaultMessage); // 將初始跑馬燈訊息設置為資訊欄的內容
  }, [displayLanguage]);

  useEffect(() => {
    const fetchHymnList = async () => {
      try {
        const hymnCollection = collection(db, 'hymnlist');
        const snapshot = await getDocs(hymnCollection);
        const fetchedHymnList = snapshot.docs.map(doc => {
        const data = doc.data();
        return { ...data, 编号: (data.编号 ?? '').toString() };
      });
        // 初始值按编号由大到小排列
        fetchedHymnList.sort((a,b)=>compareBianHaoRaw(a.编号, b.编号, 'desc'));
        setHymnList(fetchedHymnList);
        setOriginalHymnList(fetchedHymnList);
        // ✅ 保存原始歌單副本，避免頻繁存取Firebase
        setOriginalHymnListCopy(JSON.parse(JSON.stringify(fetchedHymnList)));

        // 🔴 URL 參數處理：?hymn=0918&mode=sing
        const params = new URLSearchParams(window.location.search);
        const hymnParam = params.get('hymn');
        const modeParam = params.get('mode');

        const modeToCategory = {
          'sing':  '原唱链结',
          'ktv':   'KTV链结',
          'cover': '翻唱链结',
          'short': '短视频链结',
          'ref':   '参考链结',
        };

        if (hymnParam) {
          const targetIndex = fetchedHymnList.findIndex(h => h.编号 === hymnParam.toString());
          if (targetIndex !== -1) {
            const targetHymn = fetchedHymnList[targetIndex];
            const category = modeToCategory[modeParam] || '参考链结';
            const videoUrl = targetHymn[category] || targetHymn['参考链结'] || '';

            setCurrentIndex(targetIndex);
            setCurrentSong(targetHymn);
            setCurrentCategory(category);
            setCurrentVideoId(videoUrl);
            const cleanUrl = extractMusicUrl(videoUrl);
            setCurrentPlayingUrl(cleanUrl || videoUrl);

            // 更新按鈕顏色對應選取的 mode
            setButtonColor(prev => ({
              ...prev,
              sing:          category === '原唱链结'   ? 'deepskyblue' : 'bisque',
              accompaniment: category === 'KTV链结'    ? 'deepskyblue' : 'bisque',
              cover:         category === '翻唱链结'   ? 'deepskyblue' : 'bisque',
              shortVideo:    category === '短视频链结' ? 'deepskyblue' : 'bisque',
              referenceVideo:category === '参考链结'   ? 'deepskyblue' : 'bisque',
            }));
            setLoading(false);
            return;
          }
        }

        // 沒有 URL 參數，正常預設第一首
        setCurrentVideoId(fetchedHymnList[0]?.参考链结 || '');
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };

    fetchHymnList();
  }, []);

  // ✅ 新增：根據翻唱歌曲模式更新歌單的翻唱鏈結
  const updateHymnListByCoverMode = () => {
    if (!originalHymnListCopy || originalHymnListCopy.length === 0) return;
    
    const updatedList = originalHymnListCopy.map(hymn => {
      const newHymn = { ...hymn };
      const songId = hymn.编号;
      
      if (coverSongMode === 1) {
        // 模式1: 只用自己輸入的YouTube鏈結，清空系統內建的翻唱鏈結
        newHymn.翻唱链结 = userCoverLinks[songId] || '';
      } else if (coverSongMode === 2) {
        // 模式2: 優先使用自己輸入的YouTube鏈結
        if (userCoverLinks[songId]) {
          newHymn.翻唱链结 = userCoverLinks[songId];
        } else {
          // 從原始副本恢復系統內建的翻唱鏈結
          const originalHymn = originalHymnListCopy.find(h => h.编号 === songId);
          newHymn.翻唱链结 = originalHymn?.翻唱链结 || '';
        }
      } else if (coverSongMode === 3) {
        // 模式3: 使用系統內建翻唱歌曲，從原始副本恢復
        const originalHymn = originalHymnListCopy.find(h => h.编号 === songId);
        newHymn.翻唱链结 = originalHymn?.翻唱链结 || '';
      }
      
      return newHymn;
    });
    
    setOriginalHymnList(updatedList);
    
    // 如果當前顯示的是完整歌單，也更新 hymnList
    if (hymnList.length === originalHymnListCopy.length) {
      setHymnList(updatedList);
    }
  };

  // ✅ 當翻唱歌曲模式或使用者翻唱連結改變時，更新歌單
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    updateHymnListByCoverMode();
  }, [coverSongMode, userCoverLinks, originalHymnListCopy]);

  const urlHymnHandled = React.useRef(false);

  useEffect(() => {
    if (!isSearching) {
      // 🔴 URL 參數的第一次載入由 fetchHymnList 處理，跳過避免覆蓋
      const params = new URLSearchParams(window.location.search);
      if (params.get('hymn') && !urlHymnHandled.current) {
        urlHymnHandled.current = true;
        return;
      }
      let videoId = hymnList[currentIndex]?.[currentCategory] || '';
      setCurrentVideoId(videoId);
      // 🔴 修正：同步更新 currentPlayingUrl，否則 HybridMusicPlayer 不會切換到下一首歌
      // (此前只有 handlePrevious/handleNext/點選清單會更新 currentPlayingUrl，
      //  但歌曲自然播放結束 handleEnd 只改變 currentIndex，導致左側播放器卡在原曲)
      const cleanVideoId = extractMusicUrl(videoId);
      setCurrentPlayingUrl(cleanVideoId || videoId);
    }
  }, [currentIndex, hymnList, currentCategory, isSearching]);

  useEffect(() => {
    if (searchedSong) {
      addDoc(collection(db, 'hymnlist'), searchedSong)
        .then(() => {

        })
        .catch((error) => {
          console.error("Error adding searched song to database: ", error);
        })
        .finally(() => {
          setSearchedSong(null);
        });
    }
  }, [searchedSong]);

 

    // 创建一个新的 useEffect 钩子，监听 currentIndex 和 isSearching 的变化
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
    // 根据 currentIndex 获取当前歌曲信息，并更新 currentSong 的值为该歌曲信息
    setCurrentSong(hymnList[currentIndex]);
    }, [currentIndex, isSearching]);

    useEffect(() => {
        // 确保 hymnList 已加载
        // 🔴 如果 URL 有 hymn= 參數，跳過初始化（由 fetchHymnList 處理）
        const params = new URLSearchParams(window.location.search);
        if (params.get('hymn')) return;

        if (hymnList.length > 0) {
          // 更新 currentSong 的值为第一首歌曲的信息
          setCurrentSong(hymnList[0]);
          // 手动触发自动播放第一首歌曲（使用原唱链结）
          const firstSongUrl = hymnList[0]?.原唱链结 || '';
          setCurrentVideoId(firstSongUrl);
          // 🔴 新增：初始化播放 URL
          const cleanUrl = extractMusicUrl(firstSongUrl);
          setCurrentPlayingUrl(cleanUrl || firstSongUrl);
        }
      }, [hymnList]);
  
  const handleSelectSongs = () => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式 
    setShowLspColumn(false);
    const currentSelectSongs = originalHymnList.filter(hymn => hymn.精选 === 'y');
    // 更新当前歌曲列表和按钮名称

    setHymnList(currentSelectSongs);
    setSortOrder('desc');
    setCurrentIndex(0);
    setCurrentCategory('原唱链结');
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
    //setCurrentVideoId(shuffledSongs[0]?.参考链结 || '');
    setButtonColor(prevState => ({
      ...prevState,
      sing: 'deepskyblue',  //導唱
      accompaniment: 'bisque', //伴奏
      cover: 'bisque',      //翻唱
      shortVideo: 'bisque', //短視頻
      referenceVideo: 'bisque', //參考視頻
      search: 'darkorange', //搜尋
      all: 'yellow',   //全部歌曲
      cantonese: 'yellow',  //粵語歌曲
      english: 'yellow',    //英文歌曲
      select: 'deepskyblue',     //精選
      highquility: 'yellow', //優質伴奏
      harmony: 'yellow',    //和聲歌曲
      custom1: 'orange',    //自選歌單1
      custom2: 'orange',    //自選歌單2
      custom3: 'orange',    //自選歌單3
      custom4: 'orange',    //自選歌單4
      custom5: 'orange',    //自選歌單5
      worship: 'gold',      //敬拜
      praise: 'gold',       //讚美
      thanks: 'gold',       //感恩
      desire: 'gold',       //生命圣诗
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5

    }));
  };
  
  const handleHighQuilityAccmpaniment = () => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式
    setShowLspColumn(false);
    const currentSelectSongs = originalHymnList.filter(hymn => hymn.伴奏 === 'y');
    // 更新当前歌曲列表和按钮名称

    setHymnList(currentSelectSongs);
    setSortOrder('desc');
    setCurrentIndex(0);
    setCurrentCategory('原唱链结');
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
    //setCurrentVideoId(shuffledSongs[0]?.参考链结 || '');
    setButtonColor(prevState => ({
      ...prevState,
      sing: 'deepskyblue',  //導唱
      accompaniment: 'bisque', //伴奏
      cover: 'bisque',      //翻唱
      shortVideo: 'bisque', //短視頻
      referenceVideo: 'bisque', //參考視頻
      search: 'darkorange', //搜尋
      all: 'yellow',   //全部歌曲
      cantonese: 'yellow',  //粵語歌曲
      english: 'yellow',    //英文歌曲
      select: 'yellow',     //精選
      highquility: 'deepskyblue', //優質伴奏
      harmony: 'yellow',    //和聲歌曲
      custom1: 'orange',    //自選歌單1
      custom2: 'orange',    //自選歌單2
      custom3: 'orange',    //自選歌單3
      custom4: 'orange',    //自選歌單4
      custom5: 'orange',    //自選歌單5
      worship: 'gold',      //敬拜
      praise: 'gold',       //讚美
      thanks: 'gold',       //感恩
      desire: 'gold',       //生命圣诗
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    }));
  };

  const handleHarmony = () => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式    
    setShowLspColumn(false);
    const currentSelectSongs = originalHymnList.filter(hymn => hymn.和声 !== '');
    // 更新当前歌曲列表和按钮名称
    setSortOrder('desc');
    setHymnList(currentSelectSongs);
    setCurrentIndex(0);
    setCurrentCategory('原唱链结');
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
    //setCurrentVideoId(shuffledSongs[0]?.参考链结 || '');
    setButtonColor(prevState => ({
      ...prevState,
      sing: 'deepskyblue',  //導唱
      accompaniment: 'bisque', //伴奏
      cover: 'bisque',      //翻唱
      shortVideo: 'bisque', //短視頻
      referenceVideo: 'bisque', //參考視頻
      search: 'darkorange', //搜尋
      all: 'yellow',   //全部歌曲
      cantonese: 'yellow',  //粵語歌曲
      english: 'yellow',    //英文歌曲
      select: 'yellow',     //精選
      highquility: 'yellow', //優質伴奏
      harmony: 'deepskyblue',    //和聲歌曲
      custom1: 'orange',    //自選歌單1
      custom2: 'orange',    //自選歌單2
      custom3: 'orange',    //自選歌單3
      custom4: 'orange',    //自選歌單4
      custom5: 'orange',    //自選歌單5
      worship: 'gold',      //敬拜
      praise: 'gold',       //讚美
      thanks: 'gold',       //感恩
      desire: 'gold',       //生命圣诗
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    }));
  };


    
  // 在handleClick函数中更新currentSong的值为当前点击的歌曲信息
  const handleClick = (link, index) => {
    // 判斷是否正處於 iPad 連續播放模式
// 新增：如果從 iPad 模式切換回來，恢復備份的按鈕顏色
    if (playlistActive) {
      if (Object.keys(backupButtonColor).length > 0) {
        setButtonColor(backupButtonColor);
      }
      setPlaylistActive(false); // 關鍵：切換回單曲模式
      setBackupButtonColor({}); // 🔴 清空備份，方便下次重新備份
    }

    if (index !== currentIndex || isSearching) {
      setIsSearching(false);
      setPlayerTime(0); // 从头开始播放
    }
    setCurrentIndex(index);
	setCurrentVideoId(link);
    
    // 🔴 新增：设置 currentPlayingUrl 以支持 HybridMusicPlayer
    const cleanUrl = extractMusicUrl(link);
    setCurrentPlayingUrl(cleanUrl || link);
    //console.log('🎵 点击播放列表，设置 URL:', cleanUrl || link);

    
    // ✅ 檢查是否為翻唱模式且使用者不是william
    let videoId = link;
    
    // ✅ 如果是翻唱鏈結但沒有資料,自動切換到參考視頻
    if (currentCategory === '翻唱链结' && (!videoId || videoId.trim() === '')) {
      setCurrentCategory('参考链结');
      setButtonColor(prev => ({
        ...prev,
        cover: 'bisque',
        referenceVideo: 'deepskyblue'
      }));
      videoId = hymnList[index]?.参考链结 || '';
    }
    
    setCurrentVideoId(videoId);
    // 🔴 修正：若因翻唱鏈結缺失而改用參考視頻，需同步更新 currentPlayingUrl
    const finalCleanUrl = extractMusicUrl(videoId);
    setCurrentPlayingUrl(finalCleanUrl || videoId);
    setIs20SecondsLooping(false);  // 在點擊資料表時設置20秒循環按鈕的狀態為False,恢復按鍵初始顏色
    // 更新 currentSong 为当前点击的歌曲信息
    setCurrentSong(hymnList[index]);
    // 獲取播放器的當前時間並更新 playerTime 狀態
    const player = document.getElementById('youtube-player');
    if (player) {
      setPlayerTime(player.getCurrentTime());
    }
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息  
    setButtonColor(prevState => ({  //可能是由iPad連續播放回到歌曲清單播放模式,所以將ipadPlay按鍵顏色恢復
      ...prevState,
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    }));



  };

  const handleEnd = () => {
    if (!isSearching) { // 如果不是在搜索状态下
      if (isLooping) { // 如果启用了单曲循环
        setPlayerTime(0); // 重新播放当前歌曲
      } else if (is20SecondsLooping) { // 在 20 秒循环状态下处理结束事件
        const loopStartTime = Math.max(playerTime - 20, 0); // 计算循环开始时间
        setPlayerTime(loopStartTime); // 设置播放器的当前时间为循环开始时间
        if (player) {
          player.seekTo(loopStartTime); // 播放器跳转到循环开始时间
          player.playVideo(); // 播放视频
        }
      } else {
        setCurrentIndex((currentIndex + 1) % hymnList.length); // 跳到下一首歌
        setPlayerTime(0); // 从头开始播放
      }
    } else {
      const resetVideoId = hymnList[0]?.[currentCategory] || '';
      setCurrentVideoId(resetVideoId);
      // 🔴 修正：同步更新 currentPlayingUrl，確保 HybridMusicPlayer 也跟著切換
      const cleanResetUrl = extractMusicUrl(resetVideoId);
      setCurrentPlayingUrl(cleanResetUrl || resetVideoId);
      setPlayerTime(0);
      setIsSearching(false);
    }
  };
  

  const handleCategoryChange = async (category) => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式
    setButtonColor(prevState => ({
      ...prevState,
      sing: category === '原唱链结' ? 'deepskyblue' : 'bisque',
      accompaniment: category === 'KTV链结' ? 'deepskyblue' : 'bisque',
      cover: category === '翻唱链结' ? 'deepskyblue' : 'bisque',
      shortVideo: category === '短视频链结' ? 'deepskyblue' : 'bisque',
      referenceVideo: category === '参考链结' ? 'deepskyblue' : 'bisque',
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    }));
    setIs20SecondsLooping(false); // 切换为非循环状态
    setCurrentCategory(category); // 设置当前类别
    if (category !== '短视频链结') {
      const currentTime = player ? player.getCurrentTime() : 0; // 获取当前播放时间
      setPlayerTime(currentTime); // 设置播放器的当前时间为相同的时间
      //setPlayerTime(adjustedTime); // 设置播放器的当前时间为调整后的时间
      // 等待新视频加载到缓冲区
      //await waitForBuffering();
      const videoUrl = hymnList[currentIndex]?.[category] || '';
      setCurrentVideoId(videoUrl);
      // 🔴 清理 URL 並設置當前播放 URL
      const cleanUrl = extractMusicUrl(videoUrl);
      setCurrentPlayingUrl(cleanUrl || videoUrl);
    } else {
      setPlayerTime(0); // 如果是短视频链结类别，将播放时间设为 0
      const videoUrl = hymnList[currentIndex]?.[category] || '';
      setCurrentVideoId(videoUrl);
      // 🔴 清理 URL 並設置當前播放 URL
      const cleanUrl = extractMusicUrl(videoUrl);
      setCurrentPlayingUrl(cleanUrl || videoUrl);
    }
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
  };
  
  
  const handlePlayerStateChange = (event) => {
    const newPlayerState = event.data;
    if (newPlayerState === YouTube.PlayerState.PLAYING && lastPlayerState !== YouTube.PlayerState.PLAYING) {
      const currentTime = event.target.getCurrentTime();
      if (Math.abs(playerTime - currentTime) > 1) {
        setPlayerTime(currentTime);
      }
    }
    setLastPlayerState(newPlayerState);
  };

  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
  };


// 當搜尋到歌曲時，重新設置資訊欄的內容為初始的跑馬燈訊息
const handleSearch = () => {
  const trimmedSearchTerm = searchTerm.trim();
  // 检查是否有输入文字
  if (trimmedSearchTerm === '') {
    setSearchInfo(displayLanguage === '简体' ? '找不到符合条件的歌曲' : 
                  displayLanguage === '繁體' ? '找不到符合條件的歌曲' : 
                  'No matching songs found');
    return;
  }

  // 🔹 先把輸入當作繁體 → 轉成簡體
  const simplifiedTerm = t2s(trimmedSearchTerm);

  // 🔹 建立搜尋詞組，包含簡體輸入 & 你/祢 互換版本
  let searchVariants = [simplifiedTerm];
  if (simplifiedTerm.includes('你')) {
    searchVariants.push(simplifiedTerm.replace(/你/g, '祢'));
  }
  if (simplifiedTerm.includes('祢')) {
    searchVariants.push(simplifiedTerm.replace(/祢/g, '你'));
  }

  // 🔹 過濾符合的歌曲（任一 variant 符合即可）
  const filteredHymnList = originalHymnList.filter(hymn => {
    return searchVariants.some(term => {
      const termLower = term.toLowerCase();
      return (
        hymn.编号.toString().includes(term) ||
        hymn.歌名.includes(term) ||
        hymn.繁體歌名.includes(term) ||
        hymn.英文歌名.toLowerCase().includes(termLower) ||
        hymn.原唱.includes(term) ||
        hymn.繁體原唱.includes(term) ||
        hymn.字首.toLowerCase().includes(termLower)
      );
    });
  });

  if (filteredHymnList.length > 0) {
    const currentSong = hymnList[currentIndex];
    // 设置搜索结果为歌单
    setHymnList(filteredHymnList);
    // 设置为搜索状态
    setIsSearching(true);
    setSortOrder('desc'); //歌曲指標恢復由大到小
    // 设置当前播放的歌曲为搜索结果的第一首歌曲
    setCurrentIndex(filteredHymnList.length > 1 ? 1 : 0);
    setPlayerTime(0);
    const searchVideoUrl = currentSong ? currentSong[currentCategory] : filteredHymnList[0].参考链结;
    setCurrentVideoId(searchVideoUrl);
    // 🔴 清理 URL 並設置當前播放 URL
    const cleanUrl = extractMusicUrl(searchVideoUrl);
    setCurrentPlayingUrl(cleanUrl || searchVideoUrl);
    setSearchInfo(defaultSearchInfo);
    setButtonColor(prevState => ({
      ...prevState,
      search: filteredHymnList.length > 0 ? 'deepskyblue' : 'darkorange',
      sing: 'bisque',
      accompaniment: 'bisque',
      cover: 'bisque',
      shortVideo: 'bisque',
      referenceVideo: 'bisque',
      all: 'yellow',
      cantonese: 'yellow',
      english: 'yellow',
      select: 'yellow',
      highquility: 'yellow',
      harmony: 'yellow',
      custom1: 'orange',
      custom2: 'orange',
      custom3: 'orange',
      custom4: 'orange',
      custom5: 'orange',
      worship: 'gold',
      praise: 'gold',
      thanks: 'gold',
      desire: 'gold',
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    })); 
  } else {
    setSearchInfo(displayLanguage === '简体' ? '找不到符合条件的歌曲' : 
                  displayLanguage === '繁體' ? '找不到符合條件的歌曲' : 
                  'No matching songs found');
  }
};

    
  // 切换排序方式
  const handleSort = () => {
    
    setSortColumn('编号');
if (sortOrder === 'asc') {
      sortByRandom();
    } else if (sortOrder === 'random') {
      sortByDesc();
    } else {
      sortByAsc();
    }
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
  };

  // 定义由小到大排序函数
  const sortByAsc = () => {
    const sortedList = [...hymnList].sort((a,b)=>compareBianHaoRaw(a.编号, b.编号, 'asc'));
    setHymnList(sortedList);
    setSortOrder('asc');
    setCurrentIndex(0);
    setPlayerTime(0);
  };

  // 定义由大到小排序函数
  const sortByDesc = () => {
    const sortedList = [...hymnList].sort((a,b)=>compareBianHaoRaw(a.编号, b.编号, 'desc'));
    setHymnList(sortedList);
    setSortOrder('desc');
    setCurrentIndex(0);
    setPlayerTime(0);
  };

  // 定义乱序排序函数
  const sortByRandom = () => {
    const sortedList = [...hymnList].sort(() => Math.random() - 0.5);
    setHymnList(sortedList);
    setSortOrder('random');
    setCurrentIndex(0);
    setPlayerTime(0);
  };


// ===== Added helpers for sorting by '歌名' / '原唱' / '英文歌名' with arrow indicators =====
const getFieldValue = (h, field) => {
  if (field === '编号') return h.编号;
  if (field === '歌名') {
    if (displayLanguage === 'English') return (h.英文歌名 || '');
    if (displayLanguage === '繁體') return (h.繁體歌名 || h.歌名 || '');
    return (h.歌名 || '');
  }
  if (field === '原唱') {
    if (displayLanguage === 'English') return (h.英文原唱 || h.原唱 || '');
    if (displayLanguage === '繁體') return (h.繁體原唱 || h.原唱 || '');
    return (h.原唱 || '');
  }
  if (field === '英文歌名') {
    return (h.英文歌名 || '');
  }
  if (field === '生命圣诗') {
    return (h.生命圣诗 || '');
  }
  return '';
};

const sortByField = (field, direction) => {
  const list = [...hymnList];
  if (field === '编号') {
    list.sort((a, b) => (direction === 'asc' ? (compareBianHaoRaw(a.编号, b.编号, 'asc')) : (compareBianHaoRaw(a.编号, b.编号, 'desc'))));
  } else {
    list.sort((a, b) => {
      const av = (getFieldValue(a, field) + '').toString();
      const bv = (getFieldValue(b, field) + '').toString();
      // localeCompare for robust string sorting
      const cmp = av.localeCompare(bv, 'zh-Hans-TW-u-kf-upper', { sensitivity: 'base', numeric: true });
      return direction === 'asc' ? cmp : -cmp;
    });
  }
  setHymnList(list);
  setCurrentIndex(0);
  setPlayerTime(0);
  setSearchInfo(defaultSearchInfo);
};

const handleSortBy = (field) => {
  // For non-number fields, toggle asc <-> desc; no random
  let nextDir = 'asc';
  if (sortColumn === field) {
    nextDir = (sortOrder === 'asc') ? 'desc' : 'asc';
  }
  setSortColumn(field);
  setSortOrder(nextDir);
  sortByField(field, nextDir);
};

const getSortIcon = (field) => {
  if (sortColumn !== field) return null;
  if (field === '编号') {
    if (sortOrder === 'asc') return <span>&nbsp;▲</span>;
    if (sortOrder === 'desc') return <span>&nbsp;▼</span>;
    return <span>&nbsp;🔀</span>; // random
  } else {
    return sortOrder === 'asc' ? <span>&nbsp;▲</span> : <span>&nbsp;▼</span>;
  }
};
// ===== End of added helpers =====







  const handleEnterKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // 在 handleCantoneseSongs 函数中处理粵語歌單的點擊事件
  const handleCantoneseSongs = () => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式
    setShowLspColumn(false);
    const cantoHymnList = originalHymnList.filter(hymn => hymn.粤语歌词 === 'y' || hymn.粤语歌词 === '同');
    setHymnList(cantoHymnList);
    setSortOrder('desc');
    setCurrentIndex(0);
    setCurrentCategory('原唱链结');
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
    setButtonColor(prevState => ({
      ...prevState,
      sing: 'deepskyblue',  //導唱
      accompaniment: 'bisque', //伴奏
      cover: 'bisque',      //翻唱
      shortVideo: 'bisque', //短視頻
      referenceVideo: 'bisque', //參考視頻
      search: 'darkorange', //搜尋
      all: 'yellow',   //全部歌曲
      cantonese: 'deepskyblue',  //粵語歌曲
      english: 'yellow',    //英文歌曲
      select: 'yellow',     //精選
      highquility: 'yellow', //優質伴奏
      harmony: 'yellow',    //和聲歌曲
      custom1: 'orange',    //自選歌單1
      custom2: 'orange',    //自選歌單2
      custom3: 'orange',    //自選歌單3
      custom4: 'orange',    //自選歌單4
      custom5: 'orange',    //自選歌單5
      worship: 'gold',      //敬拜
      praise: 'gold',       //讚美
      thanks: 'gold',       //感恩
      desire: 'gold',       //生命圣诗
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    }));

  };

  // 在 handleEnglishSongs 函数中处理英文歌單的點擊事件
  const handleEnglishSongs = () => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式    
    setShowLspColumn(false);
    const englishHymnList = originalHymnList.filter(hymn => hymn.英语歌词 === 'y');
    setHymnList(englishHymnList);
    setSortOrder('desc');
    setCurrentIndex(0);
    setCurrentCategory('原唱链结');
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
    setButtonColor(prevState => ({
      ...prevState,
      sing: 'deepskyblue',  //導唱
      accompaniment: 'bisque', //伴奏
      cover: 'bisque',      //翻唱
      shortVideo: 'bisque', //短視頻
      referenceVideo: 'bisque', //參考視頻
      search: 'darkorange', //搜尋
      all: 'yellow',   //全部歌曲
      cantonese: 'yellow',  //粵語歌曲
      english: 'deepskyblue',    //英文歌曲
      select: 'yellow',     //精選
      highquility: 'yellow', //優質伴奏
      harmony: 'yellow',    //和聲歌曲
      custom1: 'orange',    //自選歌單1
      custom2: 'orange',    //自選歌單2
      custom3: 'orange',    //自選歌單3
      custom4: 'orange',    //自選歌單4
      custom5: 'orange',    //自選歌單5
      worship: 'gold',      //敬拜
      praise: 'gold',       //讚美
      thanks: 'gold',       //感恩
      desire: 'gold',       //生命圣诗
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    }));


  };

    // 🔴 新增：從 Firestore 加載 YouTube 播放清單
  const loadYoutubePlaylists = async (userId) => {
    try {
      const userDocRef = doc(firestore, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (Array.isArray(data.youtubePlaylists)) {
          setYoutubePlaylists(data.youtubePlaylists);
        }
      }
    } catch (error) {
      console.error('Error loading YouTube playlists:', error);
    }
  };

  // 🔴 新增：加載 YouTube 播放清單 useEffect
  useEffect(() => {
    if (auth.currentUser) {
      loadYoutubePlaylists(auth.currentUser.uid);
    } else {
      setYoutubePlaylists(['', '', '', '', '']);
    }
  }, []);

  // 🔴 新增：提取播放清單 ID 的函數
  const extractPlaylistId = (url) => {
    if (!url || url.trim() === '') return '';
    url = url.trim();
    // 檢查是否已是播放清單 ID
    if (url.match(/^[A-Za-z0-9_-]{20,}$/)) {
      return url;
    }
    // 從 URL 中提取播放清單 ID
    const match = url.match(/list=([A-Za-z0-9_-]+)/);
    return match ? match[1] : '';
  };

  // 🔴 新增：處理 YouTube 播放清單按鈕點擊
  const handleYoutubePlaylistClick = (index) => {
    const playlistUrl = youtubePlaylists[index] || '';
    const playlistId = extractPlaylistId(playlistUrl);
    // 只有在 playlistActive 為 false (第一次進入) 時，才執行備份
    // 若已經是 true，則跳過備份步驟，直接執行後續的 UI 更新
    if (!playlistActive) {
      // 1. 備份目前的按鈕顏色狀態
      setBackupButtonColor({ ...buttonColor });
      setPlaylistActive(true);
    }
    
    if (!playlistId) {
      setSearchInfo(
        displayLanguage === '简体'
          ? '播放清单里面没有设定清单链结'
          : displayLanguage === '繁體'
          ? '播放清單裡面沒有設定清單鏈結'
          : 'Playlist has no configured list link'
      );
      return;
    }

    // 檢查使用者是否已登入
    if (!auth.currentUser) {
      setSearchInfo(
        displayLanguage === '简体'
          ? '请先登入帐号'
          : displayLanguage === '繁體'
          ? '請先登入帳號'
          : 'Please login first'
      );
      return;
    }

    // 啟動 YouTube 播放清單模式
    // 備份已在上面執行，不要重複
    setCurrentSong(null);
    setCurrentVideoId(undefined);

    // 在狀態中保存自訂播放清單 ID 並使用它
    // 🔴 設置自訂播放清單 ID，供 iPad 播放器使用
    window.CUSTOM_PLAYLIST_ID = playlistId;

    // 🔴 設置按鈕顏色為播放中狀態
    setButtonColor(prevState => ({
      ...prevState,
      sing: 'bisque',  //導唱
      accompaniment: 'bisque', //伴奏
      cover: 'bisque',      //翻唱
      shortVideo: 'bisque', //短視頻
      referenceVideo: 'bisque', //參考視頻
      search: 'darkorange', //搜尋
      all: 'yellow',   //全部歌曲
      cantonese: 'yellow',  //粵語歌曲
      english: 'yellow',    //英文歌曲
      select: 'yellow',     //精選
      highquility: 'yellow', //優質伴奏
      harmony: 'yellow',    //和聲歌曲
      custom1: 'orange',    //自選歌單1
      custom2: 'orange',    //自選歌單2
      custom3: 'orange',    //自選歌單3
      custom4: 'orange',    //自選歌單4
      custom5: 'orange',    //自選歌單5
      worship: 'gold',      //敬拜
      praise: 'gold',       //讚美
      thanks: 'gold',       //感恩
      desire: 'gold',       //生命圣诗
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: index === 0 ? 'deepskyblue' : 'LightSalmon',  // 🔴 播放清單1
      playlist2: index === 1 ? 'deepskyblue' : 'LightSalmon',  // 🔴 播放清單2
      playlist3: index === 2 ? 'deepskyblue' : 'LightSalmon',  // 🔴 播放清單3
      playlist4: index === 3 ? 'deepskyblue' : 'LightSalmon',  // 🔴 播放清單4
      playlist5: index === 4 ? 'deepskyblue' : 'LightSalmon',  // 🔴 播放清單5
    }));

    setSearchInfo(
      displayLanguage === '简体'
        ? `*正在播放YouTube播放清单${index + 1}`
        : displayLanguage === '繁體'
        ? `*正在播放YouTube播放清單${index + 1}`
        : `*Playing YouTube Playlist ${index + 1}`
    );
  };

  // 🔴 新增：取得播放清單按鈕的標籤
  const getPlaylistButtonLabel = (index) => {
    if (displayLanguage === '简体') {
      return `播放清单${index + 1}`;
    }
    if (displayLanguage === '繁體') {
      return `播放清單${index + 1}`;
    }
    return `List ${index + 1}`;
  };

const handleHymnPlaylist = () => {
    // 只有在 playlistActive 為 false (第一次進入) 時，才執行備份
    // 若已經是 true，則跳過備份步驟，直接執行後續的 UI 更新
    if (!playlistActive) {
      // 1. 備份目前的按鈕顏色狀態
      setBackupButtonColor({ ...buttonColor });
      setPlaylistActive(true);
    }
      
    // 關鍵：消除右側清單的選取背景色（淺藍色）
    // 這樣使用者就不會被「停在某一首」的藍色條誤導
    setCurrentSong(null);

    // 設CUSTOM_PLAYLIST_ID為空字串 (會觸發播放器 || 邏輯去抓後面的 IPAD_ID)
    window.CUSTOM_PLAYLIST_ID = "";

    // ✔ 可以留，也可以刪，已經不影響 playlist iframe
    setCurrentVideoId(undefined);

    // 在兩個清單之間切換
    const nowPlayingShortVideo = !isPlayingShortVideoPlaylist;
    setIsPlayingShortVideoPlaylist(nowPlayingShortVideo);

    // 更新 window.CUSTOM_PLAYLIST_ID 為對應的清單 ID
    if (nowPlayingShortVideo) {
      window.CUSTOM_PLAYLIST_ID = HYMN_SHORT_VIDEO_PLAYLIST_ID;  // 短視頻清單
    } else {
      window.CUSTOM_PLAYLIST_ID = HYMN_PLAYLIST_ID;  // 原唱清單
    }

    setSearchInfo(
      displayLanguage === '简体'
        ? (nowPlayingShortVideo 
          ? '*正在播放诗歌短视频清单'
          : '*正在播放诗歌原唱清单')
        : displayLanguage === '繁體'
        ? (nowPlayingShortVideo
          ? '*正在播放詩歌短視頻清單'
          : '*正在播放詩歌原唱清單')
        : (nowPlayingShortVideo
          ? '* Currently playing the short video song list.'
          : '* Currently playing the original song list.')
    );

    setButtonColor(prevState => ({
      ...prevState,
      sing: 'bisque',  //導唱
      accompaniment: 'bisque', //伴奏
      cover: 'bisque',      //翻唱
      shortVideo: 'bisque', //短視頻
      referenceVideo: 'bisque', //參考視頻
      search: 'darkorange', //搜尋
      all: 'yellow',   //全部歌曲
      cantonese: 'bisque',  //粵語歌曲
      english: 'yellow',    //英文歌曲
      select: 'yellow',     //精選
      highquility: 'yellow', //優質伴奏
      harmony: 'yellow',    //和聲歌曲
      custom1: 'orange',    //自選歌單1
      custom2: 'orange',    //自選歌單2
      custom3: 'orange',    //自選歌單3
      custom4: 'orange',    //自選歌單4
      custom5: 'orange',    //自選歌單5
      worship: 'gold',      //敬拜
      praise: 'gold',       //讚美
      thanks: 'gold',       //感恩
      desire: 'gold',       //生命圣诗
      ipadPlay: 'deepskyblue', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    }));
  };

  const exitIPADPlaylistMode = () => {
    if (playlistActive) {
      setPlaylistActive(false);
      setCurrentVideoId(null);   // 或 ''
      setPlayerTime(0);
    }
  };




const handleBackward20Seconds = () => {
    if (playlistActive) {
      alert(displayLanguage === '简体' ? '播放清单模式下不支援跳轉' : '播放清單模式下不支援跳轉');
      return;
    }
    if (player) {
      const currentTime = player.getCurrentTime(); // 直接獲取播放器實際時間
      const newTime = Math.max(currentTime - 20, 0);
      player.seekTo(newTime); // 強制播放器跳轉
      setPlayerTime(newTime);
      setSearchInfo(defaultSearchInfo);
    }
  };

  const handleBackward10Seconds = () => {
    if (playlistActive) return;
    if (player) {
      const currentTime = player.getCurrentTime();
      const newTime = Math.max(currentTime - 10, 0);
      player.seekTo(newTime);
      setPlayerTime(newTime);
      setSearchInfo(defaultSearchInfo);
    }
  };

  const handleForward10Seconds = () => {
    if (playlistActive) return;
    if (player) {
      const currentTime = player.getCurrentTime();
      const newTime = currentTime + 10;
      player.seekTo(newTime);
      setPlayerTime(newTime);
      setSearchInfo(defaultSearchInfo);
    }
  };

  const handleForward20Seconds = () => {
    if (playlistActive) return;
    if (player) {
      const currentTime = player.getCurrentTime();
      const newTime = currentTime + 20;
      player.seekTo(newTime);
      setPlayerTime(newTime);
      setSearchInfo(defaultSearchInfo);
    }
  };

  const handleLoadAllSongs = () => {
    exitIPADPlaylistMode();  // 退出iPad播放清單模式  
    setShowLspColumn(false);
    setHymnList(originalHymnList);
    setCurrentCategory('原唱链结');
    setSortOrder('desc');
    setCurrentIndex(0);
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
    setButtonColor(prevState => ({
      ...prevState,
      sing: 'deepskyblue',  //導唱
      accompaniment: 'bisque', //伴奏
      cover: 'bisque',      //翻唱
      shortVideo: 'bisque', //短視頻
      referenceVideo: 'bisque', //參考視頻
      search: 'darkorange', //搜尋
      all: 'deepskyblue',   //全部歌曲
      cantonese: 'yellow',  //粵語歌曲
      english: 'yellow',    //英文歌曲
      select: 'yellow',     //精選
      highquility: 'yellow', //優質伴奏
      harmony: 'yellow',    //和聲歌曲
      custom1: 'orange',    //自選歌單1
      custom2: 'orange',    //自選歌單2
      custom3: 'orange',    //自選歌單3
      custom4: 'orange',    //自選歌單4
      custom5: 'orange',    //自選歌單5
      worship: 'gold',      //敬拜
      praise: 'gold',       //讚美
      thanks: 'gold',       //感恩
      desire: 'gold',       //生命圣诗
      ipadPlay: 'LightSalmon', //iPad連續播放
      playlist1: 'LightSalmon',  // 🔴 播放清單1
      playlist2: 'LightSalmon',  // 🔴 播放清單2
      playlist3: 'LightSalmon',  // 🔴 播放清單3
      playlist4: 'LightSalmon',  // 🔴 播放清單4
      playlist5: 'LightSalmon',  // 🔴 播放清單5
    }));

  };

  const handleLoopToggle = () => {
    setIsLooping(!isLooping);
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
  };

  const handle20SecondsLoopToggle = () => {
    // 🚫 官方 playlist 不支援時間控制
    if (playlistActive) {
      setSearchInfo(
        displayLanguage === '简体'
          ? '播放清单模式不支持20秒循环'
          : displayLanguage === '繁體'
          ? '播放清單模式不支援20秒循環'
          : '20-second loop is not supported in playlist mode'
      );
      return;
    }

    // ===== 以下維持你原本的單曲邏輯 =====
    if (!is20SecondsLooping) {
      const currentTime = player ? player.getCurrentTime() : 0;
      const loopStartTime = Math.max(currentTime - 20, 0);
      if (player) {
        player.seekTo(loopStartTime);
        player.playVideo(); // 确保播放器正在播放
      }
      // 设置定时器，在20秒后自动停止播放
      const timer = setInterval(() => {
        const currentTime = player ? player.getCurrentTime() : 0;
        if (currentTime >= loopStartTime + 20) {
          player.seekTo(loopStartTime);
        }
      }, 1000);
      // 保存定时器ID，以便后续取消定时器
      setLoopTimer(timer);
    } else {
      clearInterval(loopTimer); // 停止定时器
    }
    setIs20SecondsLooping(!is20SecondsLooping); // 切换20 秒循环播放状态
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
  };
  

  




  // 控制上一首歌曲
  const handlePrevious = () => {
    // 根据当前索引判断是否为第一首歌曲，如果是则跳到最后一首歌曲，否则跳到前一首歌曲
    const newIndex = currentIndex === 0 ? hymnList.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    
    // ✅ 檢查是否為翻唱模式但沒有資料
    if (currentCategory === '翻唱链结') {
      const prevVideoId = hymnList[newIndex]?.翻唱链结 || '';
      // 如果上一首沒有翻唱鏈結，自動切換到參考視頻
      if (!prevVideoId || prevVideoId.trim() === '') {
        setCurrentCategory('参考链结');
        setButtonColor(prev => ({
          ...prev,
          cover: 'bisque',
          referenceVideo: 'deepskyblue'
        }));
        
        // 🔴 修正：設置上一首歌的參考鏈結並更新 currentPlayingUrl
        const referenceUrl = hymnList[newIndex]?.参考链结 || '';
        setCurrentVideoId(referenceUrl);
        const cleanUrl = extractMusicUrl(referenceUrl);
        setCurrentPlayingUrl(cleanUrl || referenceUrl);
        setSearchInfo(defaultSearchInfo);
        return;
      }
    }
    
    // 根据新的索引设置播放器的当前视频
    const prevVideoUrl = hymnList[newIndex]?.[currentCategory] || '';
    setCurrentVideoId(prevVideoUrl);
    // 🔴 清理 URL 並設置當前播放 URL
    const cleanUrl = extractMusicUrl(prevVideoUrl);
    setCurrentPlayingUrl(cleanUrl || prevVideoUrl);
    setSearchInfo(defaultSearchInfo);
  };

  // 控制暫停或播放
  const handlePause = () => {
    // ✅ 官方 playlist 模式下，不控制 iframe
    if (playlistActive) {
      return;
    }

   // ===== 原本單曲邏輯 =====
    if (player) {
      if (lastPlayerState === YouTube.PlayerState.PLAYING) {
        player.pauseVideo(); // 暫停播放
      } else {
        player.playVideo(); // 播放視頻
      }
    }
    setSearchInfo(defaultSearchInfo); // 重新设置为初始的跑马灯信息,用來清除註冊或找不到歌曲的訊息 
  };


  // 控制下一首歌曲
  const handleNext = () => {
    // 根据当前索引判断是否为最后一首歌曲，如果是则跳到第一首歌曲，否则跳到下一首歌曲
    const newIndex = currentIndex === hymnList.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    
    // ✅ 檢查是否為翻唱模式但沒有資料
    if (currentCategory === '翻唱链结') {
      const nextVideoId = hymnList[newIndex]?.翻唱链结 || '';
      // 如果下一首沒有翻唱鏈結，自動切換到參考視頻
      if (!nextVideoId || nextVideoId.trim() === '') {
        setCurrentCategory('参考链结');
        setButtonColor(prev => ({
          ...prev,
          cover: 'bisque',
          referenceVideo: 'deepskyblue'
        }));
        
        // 🔴 修正：設置下一首歌的參考鏈結並更新 currentPlayingUrl
        const referenceUrl = hymnList[newIndex]?.参考链结 || '';
        setCurrentVideoId(referenceUrl);
        const cleanUrl = extractMusicUrl(referenceUrl);
        setCurrentPlayingUrl(cleanUrl || referenceUrl);
        setSearchInfo(defaultSearchInfo);
        return;
      }
    }
    
    // 根据新的索引设置播放器的当前视频
    const nextVideoUrl = hymnList[newIndex]?.[currentCategory] || '';
    setCurrentVideoId(nextVideoUrl);
    // 🔴 清理 URL 並設置當前播放 URL
    const cleanUrl = extractMusicUrl(nextVideoUrl);
    setCurrentPlayingUrl(cleanUrl || nextVideoUrl);
    setSearchInfo(defaultSearchInfo);
  };



// Google帳號登入按鈕的點擊事件處理函數
const handleGoogleLogin = async () => {
  const provider = new GoogleAuthProvider();
  try {
    // ✅ 手機用 Redirect（iPhone Safari 不支援 Popup），電腦用 Popup
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      await signInWithRedirect(auth, provider);
      return; // Redirect 會離開頁面，返回後由 getRedirectResult 處理
    }
    const result = await signInWithPopup(auth, provider);
    // 登录成功后设置状态为已登录
    setIsLoggedIn(true);
    // 获取用户的额外数据
    const user = result.user;

    // 设置 displayName 状态
    setDisplayName(user.displayName);
    setEmail(user.email); // ✅ 將 email 存入狀態

    // 检查用户是否在 Firestore 中有记录
    const userDocRef = doc(firestore, 'users', user.uid);
    const userDocSnapshot = await getDoc(userDocRef);
    if (!userDocSnapshot.exists()) {
      // 如果用户记录不存在，则创建新的用户记录并设置默认自定义歌单名称
      const userData = {
        displayName: user.displayName,
        email: user.email,
        photoURL: user.photoURL,
        displayLanguage: displayLanguage, // 🔴 新增：儲存目前的語言模式
        customPlaylists: {
          playlist1: displayLanguage === '简体' ? '自选歌单1' : (displayLanguage === '繁體' ? '自選歌單1' : 'Custom Playlist 1'),
          playlist2: displayLanguage === '简体' ? '自选歌单2' : (displayLanguage === '繁體' ? '自選歌單2' : 'Custom Playlist 2'),
          playlist3: displayLanguage === '简体' ? '自选歌单3' : (displayLanguage === '繁體' ? '自選歌單3' : 'Custom Playlist 3'),
          playlist4: displayLanguage === '简体' ? '自选歌单4' : (displayLanguage === '繁體' ? '自選歌單4' : 'Custom Playlist 4'),
          playlist5: displayLanguage === '简体' ? '自选歌单5' : (displayLanguage === '繁體' ? '自選歌單5' : 'Custom Playlist 5')
        }
      };
      await setDoc(userDocRef, userData);
      setCustomPlaylistNames(Object.values(userData.customPlaylists));

    } else {
      // 如果用户记录存在，从 Firestore 获取自定义歌单名称
      const userData = userDocSnapshot.data();
      setCustomPlaylistNames(Object.values(userData.customPlaylists || {}));
      // 🔴 新增：若資料庫已有儲存語言設定，套用該設定
      if (userData.displayLanguage && languages.includes(userData.displayLanguage)) {
        setDisplayLanguage(userData.displayLanguage);
      }
    }
  } catch (error) {
    // 登录失败
    if (!isLoggedIn) {
      setSearchInfo((displayLanguage === '简体' ? 'Google账号登入失败' : (displayLanguage === '繁體' ? 'Google帳號登入失敗' : 'Google account login failed')));
    }
  }
};




const handleEmailPasswordLogin = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    // 登录成功后设置状态为已登录
    setIsLoggedIn(true);
    setSearchInfo(displayLanguage === '简体' ? '邮箱登入成功' : (displayLanguage === '繁體' ? '郵箱登入成功' : 'Email login successful'));
    // 登录成功后关闭小窗口
    toggleModal();

    // 保存用户数据到 Firestore
    const userDocRef = doc(firestore, 'users', user.uid);
    const userDocSnapshot = await getDoc(userDocRef);
    if (!userDocSnapshot.exists()) {
      // 如果用户记录不存在，则创建新的用户记录并设置默认自定义歌单名称
      const userData = {
        displayName: user.displayName,
        email: user.email,
        displayLanguage: displayLanguage, // 🔴 新增：儲存目前的語言模式
        customPlaylists: {
          playlist1: displayLanguage === '简体' ? '自选歌单1' : (displayLanguage === '繁體' ? '自選歌單1' : 'Custom Playlist 1'),
          playlist2: displayLanguage === '简体' ? '自选歌单2' : (displayLanguage === '繁體' ? '自選歌單2' : 'Custom Playlist 2'),
          playlist3: displayLanguage === '简体' ? '自选歌单3' : (displayLanguage === '繁體' ? '自選歌單3' : 'Custom Playlist 3'),
          playlist4: displayLanguage === '简体' ? '自选歌单4' : (displayLanguage === '繁體' ? '自選歌單4' : 'Custom Playlist 4'),
          playlist5: displayLanguage === '简体' ? '自选歌单5' : (displayLanguage === '繁體' ? '自選歌單5' : 'Custom Playlist 5')
        }
      };
      await setDoc(userDocRef, userData);
      setCustomPlaylistNames(Object.values(userData.customPlaylists));

    } else {
      // 如果用户记录存在，从 Firestore 获取自定义歌单名称
      const userData = userDocSnapshot.data();
      setCustomPlaylistNames(Object.values(userData.customPlaylists || {}));
      // 🔴 新增：若資料庫已有儲存語言設定，套用該設定
      if (userData.displayLanguage && languages.includes(userData.displayLanguage)) {
        setDisplayLanguage(userData.displayLanguage);
      }
    }
  } catch (error) {
    // 登录失败
    setIsLoggedIn(false);
    if (error.code === 'auth/user-not-found') {
      setSearchInfo(displayLanguage === '简体' ? '邮箱尚未注册' : (displayLanguage === '繁體' ? '郵箱尚未註冊' : 'Email has not been registered yet'));
    } else if (error.code === 'auth/wrong-password') {
      setSearchInfo(displayLanguage === '简体' ? '密码错误' : (displayLanguage === '繁體' ? '密碼錯誤' : 'Wrong password'));
    } else {
      setSearchInfo(displayLanguage === '简体' ? '邮箱或密码错误' : (displayLanguage === '繁體' ? '郵箱或密碼錯誤' : 'Email or password is incorrect'));
    }
  }
};







// 將用戶資料保存到 Firestore
const saveUserToFirestore = async (user, email, displayName, customPlaylists) => {
  try {
    const userDocRef = doc(firestore, 'users', user.uid);
    const userData = {
      email: user.email,
      displayName: displayName || user.displayName,
      displayLanguage: displayLanguage, // 🔴 新增：儲存目前的語言模式
      customPlaylists: customPlaylists.map((playlist, index) => ({
        name: playlist.name,
        songs: playlist.songs || []
      }))
    };
    await setDoc(userDocRef, userData, { merge: true });
    setDisplayName(displayName || user.displayName);
    setCustomPlaylistNames(userData.customPlaylists.map(pl => pl.name));
  } catch (error) {
    console.error('Error saving user to Firestore:', error);
  }
};

  
  

// 處理註冊按鈕點擊事件
const handleSignUp = async (email, password, displayName) => {
  try {
    // 檢查密碼是否大於等於 6 位數
    if (password.length < 6) {
      setSearchInfo(displayLanguage === '简体' ? '密码必须至少包含 6 位数' : displayLanguage === '繁體' ? '密碼必須至少包含 6 位數' : 'Password must contain at least 6 digits');
      return;
    }

    // 檢查 displayName 是否為空
    if (!displayName || displayName.trim() === '') {
      setSearchInfo(displayLanguage === '简体' ? '用户名不能为空' : displayLanguage === '繁體' ? '用戶名不能為空' : 'Display name cannot be empty');
      return;
    }

    // 使用 Firebase createUserWithEmailAndPassword 方法進行郵箱密碼註冊
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    await updateProfile(user, { displayName }); // 設置 displayName

    // 初始化自定義歌單結構
    const initialCustomPlaylists = [
      { name: displayLanguage === '简体' ? '自选歌单1' : (displayLanguage === '繁體' ? '自選歌單1' : 'Custom Playlist 1'), songs: [] },
      { name: displayLanguage === '简体' ? '自选歌单2' : (displayLanguage === '繁體' ? '自選歌單2' : 'Custom Playlist 2'), songs: [] },
      { name: displayLanguage === '简体' ? '自选歌单3' : (displayLanguage === '繁體' ? '自選歌單3' : 'Custom Playlist 3'), songs: [] },
      { name: displayLanguage === '简体' ? '自选歌单4' : (displayLanguage === '繁體' ? '自選歌單4' : 'Custom Playlist 4'), songs: [] },
      { name: displayLanguage === '简体' ? '自选歌单5' : (displayLanguage === '繁體' ? '自選歌單5' : 'Custom Playlist 5'), songs: [] }
    ];

    // 註冊成功
    setIsLoggedIn(true); // 設置為已登入
    setSearchInfo(displayLanguage === '简体' ? '邮箱注册成功' : displayLanguage === '繁體' ? '郵箱註冊成功' : 'Email registration successful');
    // 註冊成功後關閉小視窗
    toggleModal();

    // 保存用戶資料到 Firestore
    await saveUserToFirestore(user, email, displayName, initialCustomPlaylists);
  } catch (error) {
    // 註冊失敗，處理錯誤
    if (error.code === 'auth/email-already-in-use') {
      setSearchInfo(displayLanguage === '简体' ? '邮箱已经注册过' : displayLanguage === '繁體' ? '郵箱已經註冊過' : 'Email has already been registered');
    } else {
      setSearchInfo((displayLanguage === '简体' ? '注册失败' : displayLanguage === '繁體' ? '註冊失敗' : 'Registration failed') + error.message);
    }
  }
};





  

  // 在組件中定義一個函數來處理重设密碼的按鈕點擊事件
  const handleResetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      // 密碼重置郵件已發送，你可以在這裡執行相應的操作，例如顯示提示訊息
      setSearchInfo((displayLanguage === '简体' ? '密码重置邮件已发送' : (displayLanguage === '繁體' ? '密碼重置郵件已發送' : 'Password reset email sent')));
      // 密碼重置成功後關閉小視窗
      toggleModal();
    } catch (error) {
      // 處理發送密碼重置郵件的錯誤
      setSearchInfo((displayLanguage === '简体' ? '发送密码重置邮件失败' : (displayLanguage === '繁體' ? '發送密碼重置郵件失敗' : 'Failed to send password reset email')));
    }
  };

  // (handleEditPlaylist 已移除，此函數未被任何地方呼叫)

  const handleRecording = () => {
    let recordingUrl = hymnList[currentIndex]?.录音链结 || '';
    const isCopyrighted = hymnList[currentIndex]?.版权 === 'y';
    const isNotOwner = email !== 'william0326@gmail.com'; // 使用已有 email 狀態
  
    // 若版權是 y 且不是William本人，就清空錄音鏈結
    if (isCopyrighted && isNotOwner) {
      // recordingUrl = '';    //取消清空錄音鏈結
    }
  
    if (recordingUrl.trim() !== '') {
      window.open(recordingUrl, "_blank");
    } else {
      setSearchInfo(
        displayLanguage === '简体'
          ? '无此录音链结'
          : displayLanguage === '繁體'
          ? '無此錄音鏈結'
          : 'No such recording link'
      );
    }
  };
  
  
  
  
  


  // 如果歌曲有版權且登入者不是 william0326@gmail.com，則禁用「导唱/伴奏/翻唱」按鈕
  // const disableSingingButtons = hymnList[currentIndex]?.版权 === 'y' && email !== 'william0326@gmail.com';
  const disableSingingButtons = false; //改為不禁用
  
  // ✅ 翻唱按鈕是否應該被禁用 - 根據當前歌曲是否有翻唱鏈結
  const disableCoverButton = (() => {
    const currentSongCoverLink = hymnList[currentIndex]?.翻唱链结 || '';
    return !currentSongCoverLink || currentSongCoverLink.trim() === '';
  })();

  if (error) {
    return <div>Error: {error}</div>;
  }



  return (
    <>
      {/* 🔴 開機封面動畫 - 獨立於版面結構外的固定全螢幕疊加層
          刻意不放在播放器容器內部：播放器的 DOM 高度在 iframe 剛載入的瞬間
          會有一次「先較大、再收斂為正常尺寸」的跳動，若疊加層依附在那層容器上，
          會跟著一起跳動。改用 position:fixed 蓋滿整個視窗，尺寸只由 100vw/100vh 決定，
          從頭到尾固定不變，徹底跟底下版面的載入過程脫鉤，就不會有畫面震動的感覺。
          純視覺效果，不設定 currentPlayingUrl／currentVideoId，不影響底下播放器的真實狀態。 */}
      {showIntroSplash && (
        <>
          {/* 🔴 退場動畫的 @keyframes 定義：只有 0% 與 100% 兩個關鍵影格，從頭到尾
              只有「一段」連續的補間過程，不會有方向反轉造成的停頓感。
              訣竅在於 transform-origin（旋轉樞紐點）刻意設在偏離中心的位置
              （見下方 img 的 style，使用 introAnim.originX/originY），這樣單純的
              「旋轉＋縮小」組合起來，視覺上就會自然掃出一道弧線軌跡，不需要額外的
              中途轉折關鍵影格，因此沒有方向反轉、沒有停頓，從頭到尾平順流暢。
              旋轉角度與樞紐點位置都是隨機產生（見上方 introAnim 的 useState 初始化），
              每次重新整理頁面，退場路徑彎曲的方向、弧度大小、旋轉圈數都不一樣。 */}
          {/* 🔴 退場動畫：logo 先隨機繞路（弧線），再飛入 headerImage 右上角的品牌 logo 位置並縮小消失。
              三段關鍵影格：0%靜止 → 40%繞路中途點（隨機左上/右上/左下偏移）→ 100%飛抵目標並縮小。
              translate 偏移量以 vw/vh 為單位，依寬螢幕/窄螢幕選用不同目標座標。
              最終 scale(0.05) 而非 scale(0)，讓消失前有一個「縮成小點落入 logo」的視覺暗示。 */}
          <style>{`
            @keyframes introLogoExit {
              0%   { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 1; }
              40%  { transform: translate(${introAnim.detourX}vw, ${introAnim.detourY}vh) rotate(${introAnim.spinAngle * 0.4}deg) scale(0.55); opacity: 1; }
              85%  { opacity: 0.7; }
              100% { transform: translate(${introAnim.targetX}vw, ${introAnim.targetY}vh) rotate(${introAnim.spinAngle}deg) scale(0.04); opacity: 0; }
            }
          `}</style>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#d889ff', // 設定 logo512.png 圖片未覆蓋的畫面底色
              zIndex: 9999,
              pointerEvents: 'none', // 不阻擋底下版面的點擊/觸控（動畫期間使用者仍可操作）
              overflow: 'hidden',
              transition: 'opacity 0.3s ease-out',
              transitionDelay: introSplashShrink ? '1.2s' : '0s', // 退場動畫(1.5s)進行到1.2s時才開始淡出背景，與圖片同時消失於第1.5秒整
              opacity: introSplashShrink ? 0 : 1,
            }}
          >
            <img
              src="/logo512.png"
              alt="詩歌點播網"
              style={{
                // 🔴 以「視窗寬高中較小的一邊」為基準等比縮放（vmin），512x512 正方圖
                // 不論橫屏（寬>高，如電腦）或直屏（高>寬，如手機）都能完整顯示、不超出螢幕。
                height: '100vmin',
                width: '100vmin',
                maxWidth: 'none',
                objectFit: 'contain',
                transformOrigin: 'center center',
                // 🔴 退場時套用 introLogoExit（先繞路弧線再飛入目標），時長 1.5 秒
                animationName: introSplashShrink ? 'introLogoExit' : 'none',
                animationDuration: '1.5s',
                animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
                animationFillMode: 'forwards',
              }}
            />
          </div>
        </>
      )}
    <div className="container" style={{ display: 'flex', flexDirection: isWideScreen ? 'row' : 'column', height: '100vh', width: '100vw', overflow: 'hidden', boxSizing: 'border-box'}}>
      <div className="leftside-container" ref={leftContainer} style={{ minWidth: isWideScreen ? '60%' : '100%', display: 'flex', height: '100vh', flexDirection: 'column', overflowX: 'hidden', boxSizing: 'border-box' }}> {/* 畫面左側的容器 */}
      <div style={{ margin: '10px' }}>
        {/* ===== 混合音樂播放器 - 支持 VV 音樂和 YouTube ===== */}
        {!playlistActive && (
          <div key="hybrid-player" style={{ width: '100%', marginTop: '10px', position: 'relative' }}>
            {currentPlayingUrl ? (
              <HybridMusicPlayer 
                url={currentPlayingUrl}
                playerTime={playerTime}
                onEnd={handleEnd}
                onStateChange={handlePlayerStateChange}
                onReady={(event) => setPlayer(event.target)}
                containerClassName="youtubeContainer"
              />
            ) : (
              <div style={{
                width: '100%', height: '390px',
                backgroundColor: '#000',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#666', fontSize: '14px'
              }}>
                {loading ? (displayLanguage === '简体' ? '正在载入...' : displayLanguage === '繁體' ? '正在載入...' : 'Loading...') : ''}
              </div>
            )}
          </div>
        )}


        {/* ===== iPad 官方播放清單播放器 ===== */}
        {playlistActive && (() => {
          const listId = window.CUSTOM_PLAYLIST_ID || HYMN_PLAYLIST_ID;
          const iframeSrc = `https://www.youtube.com/embed/videoseries?list=${listId}&autoplay=1&playsinline=1&controls=1&fs=1&rel=0&iv_load_policy=3`;
          return (
            <div style={{ width: '100%', aspectRatio: '16/9', marginTop: '10px', backgroundColor: '#000' }}>
              <iframe
                key={`playlist-iframe-${listId}`}
                width="100%"
                height="100%"
                src={iframeSrc}
                frameBorder="0"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                title="YouTube Playlist Player"
                style={{ display: 'block', border: 'none' }}
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          );
        })()}
      </div>

        <div style={{ display: 'flex', gap: '1px', padding: '2px', marginTop: '0', marginBottom: '2px', width: '100%', boxSizing: 'border-box' }}>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.sing , fontSize: '100%', fontWeight: 'bold' }} disabled={disableSingingButtons} onClick={() => handleCategoryChange('原唱链结')}>
            {displayLanguage === '简体' ? '导唱' : (displayLanguage === '繁體' ? '導唱' : 'Sing')} 
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.accompaniment, fontSize: '100%', fontWeight: 'bold' }} disabled={disableSingingButtons} onClick={() => handleCategoryChange('KTV链结')}>
            {displayLanguage === '简体' ? '伴奏' : (displayLanguage === '繁體' ? '伴奏' : 'Accompany')} 
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.cover, fontSize: '100%', fontWeight: 'bold' }} disabled={disableCoverButton} onClick={() => handleCategoryChange('翻唱链结')}>
            {displayLanguage === '简体' ? '翻唱' : (displayLanguage === '繁體' ? '翻唱' : 'Cover')} 
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.shortVideo, fontSize: '100%', fontWeight: 'bold' }} onClick={() => handleCategoryChange('短视频链结')}>
            {displayLanguage === '简体' ? '短视频' : (displayLanguage === '繁體' ? '短視頻' : 'Short')} 
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.referenceVideo, fontSize: '100%', fontWeight: 'bold' }} onClick={() => handleCategoryChange('参考链结')}>
            {displayLanguage === '简体' ? '参考视频' : (displayLanguage === '繁體' ? '參考視頻' : 'Reference')} 
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: 'red', fontSize: '100%', fontWeight: 'bold' }} onClick={handleRecording}>
            {displayLanguage === '简体' ? '录音' : (displayLanguage === '繁體' ? '錄音' : 'Recording')} 
          </button>
          <div style={{ position: 'relative', display: 'inline-flex', flex: 2.2, minWidth: '150px', verticalAlign: 'middle', alignItems: 'center', margin: '0 1px' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchTermChange}
              onKeyDown={handleEnterKeyPress}
              style={{
                height: '30px',               // 🔼 提高高度
                width: '100%',
                fontSize: '120%',
                fontWeight: 'bold',
                marginRight: '0px',
                marginBottom: '0',
                paddingRight: '35px',         // 🔹 預留更多空間給清除按鈕
                boxSizing: 'border-box',
                verticalAlign: 'middle',
                lineHeight: '30px',           // 🔹 讓文字垂直置中
                minWidth: '0'                 // 🔹 防止 flex 溢出
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  padding: '4px 6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  color: '#999',
                  transition: 'all 0.2s ease',
                  flexShrink: 0,              // 🔹 防止按鈕被壓縮
                  zIndex: 10                  // 🔹 確保按鈕在最上層
                }}
                onMouseEnter={(e) => {
                  e.target.style.color = '#333';
                  e.target.style.transform = 'translateY(-50%) scale(1.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#999';
                  e.target.style.transform = 'translateY(-50%)';
                }}
                title={displayLanguage === '简体' ? '清除搜寻' : displayLanguage === '繁體' ? '清除搜尋' : 'Clear search'}
              >
                ⊗
              </button>
            )}
          </div>


          <button style={{ flex: 0.8, borderRadius: '0px', backgroundColor: buttonColor.search, fontSize: '100%', fontWeight: 'bold', marginBottom: '0' }} onClick={handleSearch}>
            {displayLanguage === '简体' ? '搜寻' : (displayLanguage === '繁體' ? '搜尋' : 'Search')} 
          </button>
        </div>

        <div style={{ display: 'flex', gap: '1px', padding: '2px', marginTop: '0', marginBottom: '2px', width: '100%', boxSizing: 'border-box' }}>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: 'lightpink', fontSize: '100%', fontWeight: 'bold' }} onClick={handlePrevious} disabled={playlistActive}>
            {displayLanguage === '简体' ? '上一首' : (displayLanguage === '繁體' ? '上一首' : 'Previous')} 
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: 'lightpink' , fontSize: '100%', fontWeight: 'bold' }} onClick={handlePause} disabled={playlistActive} >
            {(displayLanguage === '简体' ? '播放／暂停' : (displayLanguage === '繁體' ? '播放／暫停' : 'Play／Pause'))}
          </button>
          <button style={{flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: 'lightpink', fontSize: '100%', fontWeight: 'bold' }} onClick={handleNext} disabled={playlistActive}>
            {displayLanguage === '简体' ? '下一首' : (displayLanguage === '繁體' ? '下一首' : 'Next')} 
          </button>
          <button style={{flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: isLooping ? 'deepskyblue' : 'lightpink', fontSize: '100%', fontWeight: 'bold' }} onClick={handleLoopToggle} disabled={playlistActive}>
            {isLooping ? 
              (displayLanguage === '简体' ? '关闭循环' : (displayLanguage === '繁體' ? '關閉循環' : 'Close loop')) 
              : 
              (displayLanguage === '简体' ? '单曲循环' : (displayLanguage === '繁體' ? '單曲循環' : '1 cycle'))}
          </button>
          <div
            onClick={() => setShowInfoModal(true)}
            style={{ width: '40%', borderRadius: '0px', backgroundColor: 'orange', fontSize: '100%', fontWeight: 'bold', color: 'black', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            title={displayLanguage === '简体' ? '点击查看完整说明' : displayLanguage === '繁體' ? '點擊查看完整說明' : 'Click to view full message'}
          >
            <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
              <Marquee speed={60} delay={3} pauseOnHover={true} >
                {searchInfo}
              </Marquee>
            </div>
          </div>
        </div>

        {/* 🔴 訊息欄彈出視窗 */}
        {showInfoModal && (
          <div
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowInfoModal(false)}
          >
            <div
              style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', width: '85%', maxWidth: '500px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: 'bold', fontSize: '110%', color: '#333' }}>
                  💬 {displayLanguage === '简体' ? '说明' : displayLanguage === '繁體' ? '說明' : 'Message'}
                </span>
                <button onClick={() => setShowInfoModal(false)} style={{ border: 'none', background: '#f0f0f0', borderRadius: '50%', width: '28px', height: '28px', fontSize: '16px', cursor: 'pointer' }}>✕</button>
              </div>
              <div style={{ overflowY: 'auto', fontSize: '105%', lineHeight: '1.8', color: '#222', padding: '4px' }}>
                {searchInfo.split('\n').map((line, i) => {
                  // 計算開頭有幾個 *
                  const starMatch = line.match(/^(\*+)(.*)/);
                  // 檢查是否為數字列表項（1. 2. 3. 等）
                  const numMatch = !starMatch && line.match(/^(\d+\.\s*)(.*)/);

                  if (starMatch) {
                    const stars = starMatch[1];   // 原始的 * 或 ** 或 ***
                    const rest  = starMatch[2];   // * 後面的文字
                    // 星號寬度估算：每個 * 約 0.6em，再加右邊空白 0.25em
                    const starWidth = `${stars.length * 0.6 + 0.25}em`;
                    return (
                      <div key={i} style={{
                        paddingLeft: starWidth,      // 折行時縮排到星號後
                        textIndent: `-${starWidth}`, // 第一行往左拉回，顯示星號
                        marginBottom: '2px',
                        wordBreak: 'break-word',
                      }}>
                        <span style={{ marginRight: '0.25em' }}>{stars}</span>
                        {rest}
                      </div>
                    );
                  }

                  if (numMatch) {
                    const prefix = numMatch[1];  // e.g. "1. "
                    const rest   = numMatch[2];
                    const prefixWidth = `${prefix.length * 0.6 + 0.25}em`;
                    return (
                      <div key={i} style={{
                        paddingLeft: `calc(2em + ${prefixWidth})`,   // 比 * 再縮兩格
                        textIndent: `-${prefixWidth}`,
                        marginBottom: '2px',
                        wordBreak: 'break-word',
                      }}>
                        <span style={{ marginRight: '0.25em' }}>{prefix}</span>
                        {rest}
                      </div>
                    );
                  }

                  // 一般行
                  return (
                    <div key={i} style={{ marginBottom: '2px', wordBreak: 'break-word' }}>{line || '\u00A0'}</div>
                  );
                })}
              </div>
              <button
                onClick={() => setShowInfoModal(false)}
                style={{ marginTop: '14px', padding: '8px 20px', borderRadius: '8px', border: 'none', backgroundColor: 'orange', fontWeight: 'bold', fontSize: '100%', cursor: 'pointer', alignSelf: 'center' }}
              >
                {displayLanguage === '简体' ? '关闭' : displayLanguage === '繁體' ? '關閉' : 'Close'}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '1px', padding: '2px', marginTop: '0', marginBottom: '2px', width: '100%', boxSizing: 'border-box' }}>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: 'palegreen', fontSize: '100%', fontWeight: 'bold' }} onClick={handleBackward20Seconds} disabled={playlistActive}>
            {displayLanguage === '简体' ? '后退20秒' : (displayLanguage === '繁體' ? '後退20秒' : 'REW 20s')} 
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: 'palegreen', fontSize: '100%', fontWeight: 'bold' }} onClick={handleBackward10Seconds} disabled={playlistActive}>
            {displayLanguage === '简体' ? '后退10秒' : (displayLanguage === '繁體' ? '後退10秒' : 'REW 10s')} 
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: 'palegreen', fontSize: '100%', fontWeight: 'bold' }} onClick={handleForward10Seconds} disabled={playlistActive}>
            {displayLanguage === '简体' ? '前进10秒' : (displayLanguage === '繁體' ? '前進10秒' : 'FF 10s')}
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: 'palegreen', fontSize: '100%', fontWeight: 'bold' }} onClick={handleForward20Seconds} disabled={playlistActive} >
          {displayLanguage === '简体' ? '前进20秒' : (displayLanguage === '繁體' ? '前進20秒' : 'FF 20s')}
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: is20SecondsLooping ? 'deepskyblue' : 'palegreen', fontSize: '100%', fontWeight: 'bold' }} onClick={handle20SecondsLoopToggle} disabled={playlistActive} >
            {is20SecondsLooping ? 
              (displayLanguage === '简体' ? '关闭循环' : (displayLanguage === '繁體' ? '關閉循環' : 'Close loop')) 
              : 
              (displayLanguage === '简体' ? '循环20秒' : (displayLanguage === '繁體' ? '循環20秒' : 'Loop 20s'))}
          </button>

          {renderLoginButton()}
          {isModalOpen && (
            <LoginModal
              displayLanguage={displayLanguage}
              handleGoogleLogin={handleGoogleLogin}
              handleEmailPasswordLogin={handleEmailPasswordLogin}
              handleSignUp={handleSignUp}
              handleResetPassword={handleResetPassword}
              onClose={toggleModal}
            />
          )}
          <p style={{ display: 'flex', gap: '1px', justifyContent: 'center', alignItems: 'center', width: '25%', fontWeight: 'bold', backgroundColor: isLoggedIn ? 'SpringGreen' : 'Thistle',  margin: '0', padding: '2' }}>
            {isLoggedIn ? displayName : (displayLanguage === '简体' ? '登入可编辑歌单' : (displayLanguage === '繁體' ? '登入可編輯歌單' : 'Edit playlist'))}
          </p>
        </div>

        {/* 🔴 第4排：全部歌曲、粵語歌曲等 - 均勻分配布局 */}
        <div style={{ display: 'flex', gap: '1px', padding: '2px', marginTop: '0', marginBottom: '2px', width: '100%', boxSizing: 'border-box' }}>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.all, fontSize: '100%', fontWeight: 'bold' }} onClick={handleLoadAllSongs}>
            {displayLanguage === '简体' ? '全部歌曲' : (displayLanguage === '繁體' ? '全部歌曲' : 'All songs')}
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.cantonese, fontSize: '100%', fontWeight: 'bold' }} onClick={handleCantoneseSongs}>
            {displayLanguage === '简体' ? '粵语歌曲' : (displayLanguage === '繁體' ? '粵語歌曲' : 'Cantonese')}
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.english, fontSize: '100%', fontWeight: 'bold' }} onClick={handleEnglishSongs}>
            {displayLanguage === '简体' ? '英文歌曲' : (displayLanguage === '繁體' ? '英文歌曲' : 'English')}
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.select, fontSize: '100%', fontWeight: 'bold' }} onClick={handleSelectSongs}>
            {displayLanguage === '简体' ? '精选歌曲' : (displayLanguage === '繁體' ? '精選歌曲' : 'Featured')}
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.highquility, fontSize: '100%', fontWeight: 'bold' }} onClick={handleHighQuilityAccmpaniment}>
            {displayLanguage === '简体' ? '优质伴奏' : (displayLanguage === '繁體' ? '優質伴奏' : 'HQ accomp')}
          </button>
          <button style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.harmony, fontSize: '100%', fontWeight: 'bold' }} onClick={handleHarmony}>
            {displayLanguage === '简体' ? '和声歌曲' : (displayLanguage === '繁體' ? '和聲歌曲' : 'Harmony')}
          </button>
        </div>

        {/* 🔴 第5排：敬拜、讚美等 - 均勻分配布局 */}
        <div style={{ display: 'flex', gap: '1px', padding: '2px', marginTop: '0', marginBottom: '2px', width: '100%', boxSizing: 'border-box' }}>
          <button onClick={() => handleThematicButtonClick('敬拜')} style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.worship, fontSize: displayLanguage === 'English' ? '80%' : '100%', fontWeight: 'bold', whiteSpace: 'normal', lineHeight: 1.2 }}>{displayLanguage === '简体' ? '敬拜' : (displayLanguage === '繁體' ? '敬拜' : 'Worship')}</button>
          <button onClick={() => handleThematicButtonClick('赞美')} style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.praise, fontSize: displayLanguage === 'English' ? '80%' : '100%', fontWeight: 'bold', whiteSpace: 'normal', lineHeight: 1.2 }}>{displayLanguage === '简体' ? '赞美' : (displayLanguage === '繁體' ? '讚美' : 'Praise')}</button>
          <button onClick={() => handleThematicButtonClick('感恩')} style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.thanks, fontSize: displayLanguage === 'English' ? '80%' : '100%', fontWeight: 'bold', whiteSpace: 'normal', lineHeight: 1.2 }}>{displayLanguage === '简体' ? '感恩' : (displayLanguage === '繁體' ? '感恩' : 'Thanks')}</button>
          <button onClick={() => handleThematicButtonClick('生命圣诗')} style={{ flex: 1, margin: '0 1px', borderRadius: '10px', backgroundColor: buttonColor.desire, fontSize: displayLanguage === 'English' ? '80%' : '100%', fontWeight: 'bold', whiteSpace: 'normal', lineHeight: 1.2 }}>{displayLanguage === '简体' ? '生命圣诗' : (displayLanguage === '繁體' ? '生命聖詩' : 'LSP')}</button>
          {/* 🎵 歌詞按鈕（播放清單模式下禁用，其餘均可按） */}
          <button
            onClick={handleLyricsButtonClick}
            disabled={playlistActive}
            style={{
              flex: 1,
              margin: '0 1px',
              borderRadius: '10px',
              backgroundColor: playlistActive ? '#ccc' : 'plum',
              fontSize: displayLanguage === 'English' ? '80%' : '100%',
              fontWeight: 'bold',
              whiteSpace: 'normal',
              lineHeight: 1.2,
              cursor: playlistActive ? 'not-allowed' : 'pointer',
              opacity: playlistActive ? 0.5 : 1,
            }}
          >
            {displayLanguage === '简体' ? '歌词' : (displayLanguage === '繁體' ? '歌詞' : 'Lyrics')}
          </button>
          <button style={{ flex: 1, margin: '0 1px', backgroundColor: 'SpringGreen', fontSize: displayLanguage === 'English' ? '80%' : '100%', fontWeight: 'bold', borderRadius: '10px', whiteSpace: 'normal', lineHeight: 1.2 }} onClick={handleLanguageToggle}>
            {languages[languageIndex]}
          </button>
        </div>

        {/* 🎵 歌詞視窗 - 可拖移、可調整大小，固定在右側歌單區 */}
        {showLyricsModal && (
          <LyricsDraggableModal
            displayLanguage={displayLanguage}
            hymnList={hymnList}
            currentIndex={currentIndex}
            currentCategory={currentCategory}
            onClose={() => setShowLyricsModal(false)}
          />
        )}

        <UserDefineButton
          auth={auth}
          firestore={firestore}
          displayLanguage={displayLanguage}
          originalHymnList={originalHymnList}
          customPlaylistNames={customPlaylistNames}
          setCustomPlaylistNames={setCustomPlaylistNames} // 将 setCustomPlaylistNames 通过 props 传递给 UserDefineButton 组件
          setHymnList={setHymnList}
          setSortOrder={setSortOrder}
          setCurrentIndex={setCurrentIndex}
          setSearchInfo={setSearchInfo}
          defaultSearchInfo={defaultSearchInfo}
          setButtonColor={setButtonColor}
          buttonColor={buttonColor}
          setPlaylistActive={setPlaylistActive}
          exitIPADPlaylistMode={exitIPADPlaylistMode}
          youtubePlaylists={youtubePlaylists}        // ✅ 新增
          setYoutubePlaylists={setYoutubePlaylists}  // ✅ 新增
          fetchUserCoverLinks={fetchUserCoverLinks}  // ✅ 新增：傳遞函數
          currentCategory={currentCategory}          // ✅ 新增：傳遞當前類別
          email={email}                              // ✅ 新增：傳遞 email
          hymnList={hymnList}                        // ✅ 新增：傳遞歌曲清單
          currentIndex={currentIndex}                // ✅ 新增：傳遞當前索引
          setCurrentVideoId={setCurrentVideoId}      // ✅ 新增：傳遞設置函數
          userCoverLinks={userCoverLinks}            // ✅ 新增：傳遞使用者連結
          coverSongMode={coverSongMode}              // ✅ 新增：傳遞翻唱歌曲模式
          setCoverSongMode={setCoverSongMode}        // ✅ 新增：傳遞設置翻唱歌曲模式函數
          originalHymnListCopy={originalHymnListCopy}  // ✅ 新增：傳遞原始歌單副本
          onDisplayLanguageChange={handleDisplayLanguageChange}  // 🔴 新增：傳遞語言模式變更函數
        />

        {/* 🔴 第6排：YouTube 播放清單按鈕 + iPad連續播放縮小按鈕 */}
        <div style={{ display: 'flex', gap: '1px', padding: '2px', marginTop: '0', marginBottom: '2px', width: '100%', boxSizing: 'border-box' }}>
          {/* YouTube 播放清單按鈕 */}
          {Array.from({ length: 5 }, (_, index) => {
            // 🔴 判斷播放清單按鈕的顏色：有設定且可用=deepskyblue，否則=LightSalmon
            const playlistUrl = youtubePlaylists[index] || '';
            const hasPlaylist = playlistUrl.trim() !== '';
            const canUsePlaylist = auth.currentUser && hasPlaylist ;
            
            return (
              <button
                key={`playlist-${index}`}
                style={{ 
                  flex: 1,
                  margin: '0 1px',
                  borderRadius: '10px', 
                  //  buttonColor 狀態，點擊後顏色才會改變
                  backgroundColor: buttonColor[`playlist${index + 1}`] || 'LightSalmon',
                  fontSize: '100%', 
                  fontWeight: 'bold' 
                }}
                onClick={() => handleYoutubePlaylistClick(index)}
                disabled={!canUsePlaylist}
              >
                {getPlaylistButtonLabel(index)}
              </button>
            );
          })}
          {/* Youtube歌單按鈕 - 縮小寬度 */}
          <button
            //disabled={isIPhoneDevice}  //iPhone手機不允許執行
            style={{
              flex: 1,
              margin: '0 1px',
              //height: '30px',
              backgroundColor: buttonColor.ipadPlay || 'LightSalmon',
              fontSize: '100%',
              fontWeight: 'bold',
              borderRadius: '10px',
              //cursor: isIPhoneDevice ? 'not-allowed' : 'pointer'   //iPhone手機不允許執行
            }}
            onClick={handleHymnPlaylist}
          >
            {displayLanguage === '简体' ? '诗歌歌单' : (displayLanguage === '繁體' ? '詩歌歌單' : 'Hymn List')}
          </button>
        </div>

      </div>

      {!isWideScreen && <div className="separator" style={{ height: '5px', cursor: 'row-resize' }}></div>}
      <div className="rightside-container" ref={rightContainer} style={{ minWidth: isWideScreen ? '40%' : '100%', display: 'flex', flexDirection: 'column', height: '100vh', alignItems: 'center', justifyContent: 'flex-start', borderColor: 'grey', position: 'relative', overflow: 'hidden', boxSizing: 'border-box' }}>
        <div style={{ overflowY: 'auto', border: '1px solid black', borderRadius: '5px', width: '100%', marginBottom: '5px', marginTop: '5px', zIndex: 2, flex: 1, minHeight: 0 }}>
          <table>
            <thead style={{ backgroundColor: 'SpringGreen', position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={{ cursor: 'pointer', whiteSpace: 'nowrap'  }} onClick={handleSort}>
                  {displayLanguage === '简体' ? '编号' : (displayLanguage === '繁體' ? '編號' : 'NO')}{getSortIcon('编号')}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSortBy('歌名')}>
                  {displayLanguage === '简体' ? '歌名' : (displayLanguage === '繁體' ? '歌名' : 'Song title')}{getSortIcon('歌名')}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSortBy(showLspColumn ? '生命圣诗' : '原唱')}>
                  {showLspColumn
                    ? (displayLanguage === '简体' ? '生命圣诗' : displayLanguage === '繁體' ? '生命聖詩' : 'LSP No.')
                    : (displayLanguage === '简体' ? '原唱' : displayLanguage === '繁體' ? '原唱' : 'Singer')
                  }{getSortIcon(showLspColumn ? '生命圣诗' : '原唱')}
                </th>
                <th style={{ cursor: 'pointer' }} onClick={() => handleSortBy('英文歌名')}>
                  {displayLanguage === '简体' ? '英文歌名' : (displayLanguage === '繁體' ? '英文歌名' : 'English title')}{getSortIcon('英文歌名')}
                </th>
              </tr>
            </thead>
            <tbody>
              {hymnList.map((hymn, index) => (
                <tr key={index} ref={currentSong?.编号 === hymn.编号 ? (el => { if (el) setTimeout(() => el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 100); }) : null} style={{ borderBottom: '1px solid Black', backgroundColor: playlistActive ? 'plum' : (currentSong?.编号 === hymn.编号 ? 'lightblue' : 'plum'), fontWeight: 'bold' }} onClick={() => handleClick(hymn[currentCategory], index)}>
                  <td>{hymn.编号}</td>
                  <td>{displayLanguage === '简体' ? hymn.歌名 : hymn.繁體歌名}</td>
                  <td>{showLspColumn ? (displayLanguage === '简体' ? (hymn.生命圣诗 || '') : s2t(hymn.生命圣诗 || '')) : (displayLanguage === '简体' ? hymn.原唱 : hymn.繁體原唱)}</td>
                  <td>{hymn.英文歌名}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <img src={headerImages[displayLanguage]} alt="header" style={{ width: '100%', height: '20%', objectFit: 'contain', marginTop: '0', zIndex: 1 }} />
        
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '100%', fontWeight: 'bold', marginTop: '0', marginBottom: '10px' }}>
          {/* ✅ 版本號只需改這裡一次 ↓ */}
          {(() => {
            const rev = '1.41';
            const sep = <span style={{ display: 'inline-block', borderRight: '2px solid black', height: '12px', width: '0px', margin: '0 6px', verticalAlign: 'middle' }}></span>;
            const revLabel = displayLanguage === '简体' ? `版本: ${rev}` :
                             displayLanguage === '繁體' ? `版本: ${rev}` :
                             `Rev: ${rev}`;
            const builderLabel = displayLanguage === '简体' ? <>制作: William Tsai {sep} 建站: 2024.04.23</> :
                                 displayLanguage === '繁體' ? <>製作: William Tsai {sep} 建站: 2024.04.23</> :
                                 <>By: William Tsai {sep} Est. 2024.04.23</>;
            // 🔴 強制重新載入（清除 Service Worker 快取）
            const handleForceReload = async () => {
              try {
                // 清除所有 Service Worker 快取
                if ('caches' in window) {
                  const cacheNames = await caches.keys();
                  await Promise.all(cacheNames.map(name => caches.delete(name)));
                }
                // 取消註冊 Service Worker
                if ('serviceWorker' in navigator) {
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map(reg => reg.unregister()));
                }
              } catch (e) {
                // 忽略錯誤，直接強制重載
              }
              window.location.reload(true);
            };
            const reloadLabel = displayLanguage === '简体' ? '🔄 更新' :
                                displayLanguage === '繁體' ? '🔄 更新' : '🔄 Update';
            return <>{builderLabel}
              {sep}
              <a href="mailto:william0326@gmail.com" style={{ textDecoration: 'none', color: 'inherit' }}>
                <span style={{ fontSize: '100%' }}>&#x2709;</span>
              </a>
              {sep}{revLabel}
              {sep}
              <button
                onClick={handleForceReload}
                style={{
                  fontSize: '90%',
                  fontWeight: 'bold',
                  backgroundColor: 'lightyellow',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  padding: '1px 6px',
                  cursor: 'pointer',
                }}
              >{reloadLabel}</button>
            </>;
          })()}
        </div>
		
      </div>


    </div>   
    </>
  );
};



export default HymnListView;